/**
 * Outreach Send page – campaign selection, sender account selection, dry run, test send, live send, scheduling.
 */
import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import {
  Card, Select, InputNumber, Button, Space, message, Divider, Typography, Tag, Modal, Input, Spin,
  DatePicker, Table, Popconfirm, Badge, Progress, Alert, Tabs, Empty, Drawer, Form, Pagination,
} from 'antd';
import {
  SendOutlined, CheckCircleOutlined, ExperimentOutlined, EyeOutlined,
  ClockCircleOutlined, DeleteOutlined, CalendarOutlined, StopOutlined,
  LoadingOutlined, MinusOutlined, ExpandOutlined, CloseOutlined,
  FilterOutlined, TeamOutlined, SearchOutlined, ReloadOutlined,
  WarningOutlined, UnorderedListOutlined, HistoryOutlined,
  ExclamationCircleOutlined, CheckSquareOutlined, MinusSquareOutlined,
} from '@ant-design/icons';
import type {
  Campaign, SenderAccount, DryRunResponse, TestSendResponse, LiveSendResponse,
  PreviewResponse, ScheduledSend, SendJobStatus, LeadFilterParams, Lead, SendJob,
} from '../types';
import { outreachCampaignsApi, outreachSenderAccountsApi, outreachSendsApi } from '../api';
import { outreachLeadsApi } from '../api';
import { pruneMultiSelect, useCascadingFilterOptions } from '../hooks/useCascadingFilterOptions';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useTheme } from '../../../contexts/ThemeContext';
import { renderLeadStatusTag } from '../utils/leadStatus';

dayjs.extend(utc);

const { Title, Text } = Typography;

const LEAD_POTENTIAL_COLORS: Record<string, string> = {
  low: 'default',
  medium: 'blue',
  high: 'orange',
  strategic: 'purple',
};
const LAST_CONTACTED_OPTIONS = [
  { value: 3, label: 'Not in last 3 days' },
  { value: 7, label: 'Not in last 7 days' },
  { value: 14, label: 'Not in last 14 days' },
  { value: 30, label: 'Not in last 30 days' },
  { value: 60, label: 'Not in last 60 days' },
  { value: 90, label: 'Not in last 90 days' },
] as const;

// Column keys for the preview table
type PreviewColumnKey =
  'email' | 'name' | 'company' | 'role' | 'domain' | 'therapy' | 'status'
  | 'last_contacted_at' | 'country' | 'city' | 'source' | 'potential'
  | 'added_by' | 'sent' | 'next_followup_at' | 'tags';

interface ResizableHeaderCellProps extends React.ThHTMLAttributes<HTMLTableCellElement> {
  width?: number;
  onResizeStart?: (event: React.MouseEvent<HTMLSpanElement>) => void;
}

const DEFAULT_PREVIEW_COLUMN_WIDTHS: Record<PreviewColumnKey, number> = {
  email: 220,
  name: 150,
  company: 130,
  role: 130,
  domain: 130,
  therapy: 150,
  status: 100,
  last_contacted_at: 130,
  country: 100,
  city: 100,
  source: 90,
  potential: 100,
  added_by: 100,
  sent: 60,
  next_followup_at: 130,
  tags: 120,
};

const ResizableHeaderCell: React.FC<ResizableHeaderCellProps> = ({
  width,
  onResizeStart,
  children,
  style,
  ...restProps
}) => {
  const [isResizing, setIsResizing] = React.useState(false);

  const handleMouseDown = (e: React.MouseEvent<HTMLSpanElement>) => {
    setIsResizing(true);
    onResizeStart?.(e);
    const handleMouseUp = () => {
      setIsResizing(false);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    document.addEventListener('mouseup', handleMouseUp);
  };

  return (
    <th
      {...restProps}
      style={{
        ...style,
        width,
        minWidth: width,
        position: 'relative',
        whiteSpace: 'nowrap',
        padding: '12px 8px',
      }}
    >
      <span style={{ display: 'inline-block', paddingRight: onResizeStart ? 12 : 0, whiteSpace: 'nowrap' }}>
        {children}
      </span>
      {onResizeStart && (
        <span
          onMouseDown={handleMouseDown}
          className={`column-resize-handle ${isResizing ? 'is-resizing' : ''}`}
          style={{
            position: 'absolute',
            top: 0,
            right: -2,
            width: 6,
            height: '100%',
            cursor: 'col-resize',
            userSelect: 'none',
            zIndex: 10,
            transition: 'background 0.2s',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <span
            style={{
              width: 2,
              height: '100%',
              background: isResizing ? '#1890ff' : '#f0f0f0',
              transition: 'background 0.2s',
            }}
            className="handle-bar"
          />
        </span>
      )}
    </th>
  );
};

export const OutreachSend: React.FC = () => {
  const { isDark } = useTheme();
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [senderAccounts, setSenderAccounts] = useState<SenderAccount[]>([]);
  const [selectedCampaignId, setSelectedCampaignId] = useState<string>('');
  const [selectedSenderAccountId, setSelectedSenderAccountId] = useState<string>('');
  const [dryRunResult, setDryRunResult] = useState<DryRunResponse | null>(null);
  const [testSendResult, setTestSendResult] = useState<TestSendResponse | null>(null);
  const [liveSendResult, setLiveSendResult] = useState<LiveSendResponse | null>(null);
  const [testModalVisible, setTestModalVisible] = useState(false);
  const [testEmails, setTestEmails] = useState('');
  const [testSendLoading, setTestSendLoading] = useState(false);
  const [previewData, setPreviewData] = useState<PreviewResponse | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Guard against double-click / concurrent live sends
  const [liveSendLoading, setLiveSendLoading] = useState(false);

  // Lead selection / filtering state
  const [filterTherapy, setFilterTherapy] = useState<string[] | undefined>(undefined);
  const [filterCountry, setFilterCountry] = useState<string[] | undefined>(undefined);
  const [filterCity, setFilterCity] = useState<string[] | undefined>(undefined);
  const [filterLeadSource, setFilterLeadSource] = useState<string[] | undefined>(undefined);
  const [filterLeadPotential, setFilterLeadPotential] = useState<string[] | undefined>(undefined);
  const [filterAddedBy, setFilterAddedBy] = useState<string[] | undefined>(undefined);
  const [filterRole, setFilterRole] = useState<string[] | undefined>(undefined);
  const [filterDomain, setFilterDomain] = useState<string[] | undefined>(undefined);
  const [filterStatus, setFilterStatus] = useState<string | undefined>('active');
  const [filterSearch, setFilterSearch] = useState<string | undefined>(undefined);
  const [filterTags, setFilterTags] = useState<string[] | undefined>(undefined);
  const [filterLastContactedDaysAgo, setFilterLastContactedDaysAgo] = useState<number | undefined>(undefined);
  const [filterMinEmailsSent, setFilterMinEmailsSent] = useState<number | undefined>(undefined);
  const [filterMaxEmailsSent, setFilterMaxEmailsSent] = useState<number | undefined>(undefined);
  // Exclude filters
  const [excludeTherapy, setExcludeTherapy] = useState<string[] | undefined>(undefined);
  const [excludeLeadSource, setExcludeLeadSource] = useState<string[] | undefined>(undefined);
  const [excludeLeadPotential, setExcludeLeadPotential] = useState<string[] | undefined>(undefined);
  const [excludeAddedBy, setExcludeAddedBy] = useState<string[] | undefined>(undefined);
  const [excludeCountry, setExcludeCountry] = useState<string[] | undefined>(undefined);
  const [excludeCity, setExcludeCity] = useState<string[] | undefined>(undefined);
  const [excludeRole, setExcludeRole] = useState<string[] | undefined>(undefined);
  const [excludeDomain, setExcludeDomain] = useState<string[] | undefined>(undefined);
  const [excludeTags, setExcludeTags] = useState<string[] | undefined>(undefined);

  const [filteredLeads, setFilteredLeads] = useState<Lead[]>([]);
  const [filteredLeadsTotal, setFilteredLeadsTotal] = useState<number>(0);
  const [filteredLeadsLoading, setFilteredLeadsLoading] = useState(false);
  const [showLeadPreview, setShowLeadPreview] = useState(false);
  const [filterDrawerVisible, setFilterDrawerVisible] = useState(false);

  // Manual lead deselection – track the small set of IDs the user has unchecked
  const [manuallyDeselectedIds, setManuallyDeselectedIds] = useState<Set<string>>(new Set());

  const filterOptionsParams = useMemo(() => ({
    search: filterSearch,
    country: filterCountry,
    city: filterCity,
    therapy_interest: filterTherapy,
    lead_source: filterLeadSource,
    lead_potential: filterLeadPotential,
    added_by: filterAddedBy,
    role: filterRole,
    domain: filterDomain,
    status: filterStatus,
    tags: filterTags,
    last_contacted_days_ago: filterLastContactedDaysAgo,
    min_emails_sent: filterMinEmailsSent,
    max_emails_sent: filterMaxEmailsSent,
    exclude_therapy_interest: excludeTherapy,
    exclude_lead_source: excludeLeadSource,
    exclude_lead_potential: excludeLeadPotential,
    exclude_added_by: excludeAddedBy,
    exclude_country: excludeCountry,
    exclude_city: excludeCity,
    exclude_role: excludeRole,
    exclude_domain: excludeDomain,
    exclude_tags: excludeTags,
  }), [filterSearch, filterCountry, filterCity, filterTherapy, filterLeadSource,
    filterLeadPotential, filterAddedBy, filterRole, filterDomain, filterStatus, filterTags,
    filterLastContactedDaysAgo, filterMinEmailsSent, filterMaxEmailsSent,
    excludeTherapy, excludeLeadSource, excludeLeadPotential, excludeAddedBy,
    excludeCountry, excludeCity, excludeRole, excludeDomain, excludeTags]);

  const { filterOptions, loading: filterOptionsLoading } = useCascadingFilterOptions(filterOptionsParams);

  useEffect(() => {
    setFilterTherapy((v) => pruneMultiSelect(v, filterOptions.therapy_interest));
    setFilterAddedBy((v) => pruneMultiSelect(v, filterOptions.added_by));
    setFilterLeadSource((v) => pruneMultiSelect(v, filterOptions.lead_source));
    setFilterLeadPotential((v) => pruneMultiSelect(v, filterOptions.lead_potential));
    setFilterRole((v) => pruneMultiSelect(v, filterOptions.roles));
    setFilterDomain((v) => pruneMultiSelect(v, filterOptions.domains));
    setFilterCountry((v) => pruneMultiSelect(v, filterOptions.countries));
    setFilterCity((v) => pruneMultiSelect(v, filterOptions.cities));
    setFilterTags((v) => pruneMultiSelect(v, filterOptions.tags));
    setExcludeTherapy((v) => pruneMultiSelect(v, filterOptions.therapy_interest));
    setExcludeAddedBy((v) => pruneMultiSelect(v, filterOptions.added_by));
    setExcludeLeadSource((v) => pruneMultiSelect(v, filterOptions.lead_source));
    setExcludeLeadPotential((v) => pruneMultiSelect(v, filterOptions.lead_potential));
    setExcludeRole((v) => pruneMultiSelect(v, filterOptions.roles));
    setExcludeDomain((v) => pruneMultiSelect(v, filterOptions.domains));
    setExcludeCountry((v) => pruneMultiSelect(v, filterOptions.countries));
    setExcludeCity((v) => pruneMultiSelect(v, filterOptions.cities));
    setExcludeTags((v) => pruneMultiSelect(v, filterOptions.tags));
  }, [filterOptions]);

  // Live send progress – floating popup
  const [sendJobId, setSendJobId] = useState<string | null>(null);
  const [sendProgress, setSendProgress] = useState<SendJobStatus | null>(null);
  const [progressVisible, setProgressVisible] = useState(false);
  const [progressMinimized, setProgressMinimized] = useState(false);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Jobs tab state
  const [sendJobs, setSendJobs] = useState<SendJob[]>([]);
  const [sendJobsLoading, setSendJobsLoading] = useState(false);
  const jobsPollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [activeTab, setActiveTab] = useState('send');

  // Scheduling state
  const [scheduleModalVisible, setScheduleModalVisible] = useState(false);
  const [scheduledDateTime, setScheduledDateTime] = useState<dayjs.Dayjs | null>(null);
  const [scheduleMaxSends, setScheduleMaxSends] = useState<number | undefined>(undefined);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [scheduledSends, setScheduledSends] = useState<ScheduledSend[]>([]);
  const [scheduledSendsLoading, setScheduledSendsLoading] = useState(false);

  // Preview table column widths (resizable)
  const [previewColumnWidths, setPreviewColumnWidths] = useState<Record<PreviewColumnKey, number>>(DEFAULT_PREVIEW_COLUMN_WIDTHS);

  // Preview table horizontal scrollbar refs and state
  const previewTableWrapperRef = useRef<HTMLDivElement>(null);
  const previewFakeScrollRef = useRef<HTMLDivElement>(null);
  const previewFakeScrollInnerRef = useRef<HTMLDivElement>(null);
  const [isPreviewTableOverflowing, setIsPreviewTableOverflowing] = useState(false);
  const isPreviewSyncingScroll = useRef(false);

  // Build the active lead_filters object (returns undefined if no filters are set)
  const getActiveLeadFilters = useCallback((): LeadFilterParams | undefined => {
    const filters: LeadFilterParams = {};
    if (filterTherapy?.length) filters.therapy_interest = filterTherapy;
    if (filterCountry?.length) filters.country = filterCountry;
    if (filterCity?.length) filters.city = filterCity;
    if (filterLeadSource?.length) filters.lead_source = filterLeadSource;
    if (filterLeadPotential?.length) filters.lead_potential = filterLeadPotential;
    if (filterAddedBy?.length) filters.added_by = filterAddedBy;
    if (filterRole?.length) filters.role = filterRole;
    if (filterDomain?.length) filters.domain = filterDomain;
    if (filterStatus) filters.status = filterStatus;
    if (filterSearch) filters.search = filterSearch;
    if (filterTags?.length) filters.tags = filterTags;
    if (filterLastContactedDaysAgo !== undefined) filters.last_contacted_days_ago = filterLastContactedDaysAgo;
    if (filterMinEmailsSent !== undefined) filters.min_emails_sent = filterMinEmailsSent;
    if (filterMaxEmailsSent !== undefined) filters.max_emails_sent = filterMaxEmailsSent;
    // Exclude filters
    if (excludeTherapy?.length) filters.exclude_therapy_interest = excludeTherapy;
    if (excludeLeadSource?.length) filters.exclude_lead_source = excludeLeadSource;
    if (excludeLeadPotential?.length) filters.exclude_lead_potential = excludeLeadPotential;
    if (excludeAddedBy?.length) filters.exclude_added_by = excludeAddedBy;
    if (excludeCountry?.length) filters.exclude_country = excludeCountry;
    if (excludeCity?.length) filters.exclude_city = excludeCity;
    if (excludeRole?.length) filters.exclude_role = excludeRole;
    if (excludeDomain?.length) filters.exclude_domain = excludeDomain;
    if (excludeTags?.length) filters.exclude_tags = excludeTags;
    // Only return filters if at least one is set
    if (Object.keys(filters).length === 0) return undefined;
    return filters;
  }, [filterTherapy, filterCountry, filterCity, filterLeadSource, filterLeadPotential,
    filterAddedBy, filterRole, filterDomain, filterStatus, filterSearch, filterTags,
    filterLastContactedDaysAgo, filterMinEmailsSent, filterMaxEmailsSent,
    excludeTherapy, excludeLeadSource, excludeLeadPotential, excludeAddedBy,
    excludeCountry, excludeCity, excludeRole, excludeDomain, excludeTags]);

  // Lightweight: fetch only the count of matching leads (no row data)
  const fetchFilteredLeadCount = useCallback(async () => {
    setFilteredLeadsLoading(true);
    try {
      const res = await outreachLeadsApi.count({
        therapy_interest: filterTherapy,
        country: filterCountry,
        city: filterCity,
        lead_source: filterLeadSource,
        lead_potential: filterLeadPotential,
        added_by: filterAddedBy,
        role: filterRole,
        domain: filterDomain,
        status: filterStatus,
        search: filterSearch,
        tags: filterTags,
        last_contacted_days_ago: filterLastContactedDaysAgo,
        min_emails_sent: filterMinEmailsSent,
        max_emails_sent: filterMaxEmailsSent,
        exclude_therapy_interest: excludeTherapy,
        exclude_lead_source: excludeLeadSource,
        exclude_lead_potential: excludeLeadPotential,
        exclude_added_by: excludeAddedBy,
        exclude_country: excludeCountry,
        exclude_city: excludeCity,
        exclude_role: excludeRole,
        exclude_domain: excludeDomain,
        exclude_tags: excludeTags,
      });
      setFilteredLeadsTotal(res.total);
    } catch {
      message.error('Failed to count leads');
    } finally {
      setFilteredLeadsLoading(false);
    }
  }, [filterTherapy, filterCountry, filterCity, filterLeadSource, filterLeadPotential,
    filterAddedBy, filterRole, filterDomain, filterStatus, filterSearch, filterTags,
    filterLastContactedDaysAgo, filterMinEmailsSent, filterMaxEmailsSent,
    excludeTherapy, excludeLeadSource, excludeLeadPotential, excludeAddedBy,
    excludeCountry, excludeCity, excludeRole, excludeDomain, excludeTags]);

  // Preview table pagination state
  const [previewPage, setPreviewPage] = useState(1);
  const PREVIEW_PAGE_SIZE = 50;

  // Fetch a single page of lead data for the preview table
  const fetchFilteredLeads = useCallback(async (page = 1) => {
    setFilteredLeadsLoading(true);
    try {
      const res = await outreachLeadsApi.list({
        page,
        page_size: PREVIEW_PAGE_SIZE,
        therapy_interest: filterTherapy,
        country: filterCountry,
        city: filterCity,
        lead_source: filterLeadSource,
        lead_potential: filterLeadPotential,
        added_by: filterAddedBy,
        role: filterRole,
        domain: filterDomain,
        status: filterStatus,
        search: filterSearch,
        tags: filterTags,
        last_contacted_days_ago: filterLastContactedDaysAgo,
        min_emails_sent: filterMinEmailsSent,
        max_emails_sent: filterMaxEmailsSent,
        exclude_therapy_interest: excludeTherapy,
        exclude_lead_source: excludeLeadSource,
        exclude_lead_potential: excludeLeadPotential,
        exclude_added_by: excludeAddedBy,
        exclude_country: excludeCountry,
        exclude_city: excludeCity,
        exclude_role: excludeRole,
        exclude_domain: excludeDomain,
        exclude_tags: excludeTags,
      });

      setFilteredLeads(res.items);
      setFilteredLeadsTotal(res.total);
      setPreviewPage(page);
    } catch {
      message.error('Failed to fetch filtered leads');
    } finally {
      setFilteredLeadsLoading(false);
    }
  }, [filterTherapy, filterCountry, filterCity, filterLeadSource, filterLeadPotential,
    filterAddedBy, filterRole, filterDomain, filterStatus, filterSearch, filterTags,
    filterLastContactedDaysAgo, filterMinEmailsSent, filterMaxEmailsSent,
    excludeTherapy, excludeLeadSource, excludeLeadPotential, excludeAddedBy,
    excludeCountry, excludeCity, excludeRole, excludeDomain, excludeTags]);

  // Auto-fetch COUNT when filters change (debounced) – fast & lightweight
  const filterTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (filterTimerRef.current) clearTimeout(filterTimerRef.current);
    filterTimerRef.current = setTimeout(() => {
      fetchFilteredLeadCount();
      // If the preview table is already open, refresh it too (reset to page 1)
      if (showLeadPreview) {
        fetchFilteredLeads(1);
      }
    }, 400);
    return () => { if (filterTimerRef.current) clearTimeout(filterTimerRef.current); };
  }, [fetchFilteredLeadCount, fetchFilteredLeads, showLeadPreview]);

  const resetLeadFilters = () => {
    setFilterTherapy(undefined);
    setFilterCountry(undefined);
    setFilterCity(undefined);
    setFilterLeadSource(undefined);
    setFilterLeadPotential(undefined);
    setFilterAddedBy(undefined);
    setFilterRole(undefined);
    setFilterDomain(undefined);
    setFilterStatus('active');
    setFilterSearch(undefined);
    setFilterTags(undefined);
    setFilterLastContactedDaysAgo(undefined);
    setFilterMinEmailsSent(undefined);
    setFilterMaxEmailsSent(undefined);
    setExcludeTherapy(undefined);
    setExcludeLeadSource(undefined);
    setExcludeLeadPotential(undefined);
    setExcludeAddedBy(undefined);
    setExcludeCountry(undefined);
    setExcludeCity(undefined);
    setExcludeRole(undefined);
    setExcludeDomain(undefined);
    setExcludeTags(undefined);
    setShowLeadPreview(false);
    setFilteredLeads([]);
    setManuallyDeselectedIds(new Set());
    setPreviewPage(1);
  };

  // ── Sticky horizontal scrollbar sync for preview table ──
  useEffect(() => {
    if (!showLeadPreview) return; // Only sync when preview is visible
    const wrapper = previewTableWrapperRef.current;
    if (!wrapper) return;
    // Ant Design may use .ant-table-body or .ant-table-content as the scrollable container
    const tableBody = (
      wrapper.querySelector('.ant-table-body') || 
      wrapper.querySelector('.ant-table-content')
    ) as HTMLDivElement | null;
    const fakeScroll = previewFakeScrollRef.current;
    const fakeInner = previewFakeScrollInnerRef.current;
    if (!tableBody || !fakeScroll || !fakeInner) return;

    // Match fake scroll inner width to the actual table scroll width
    const syncWidth = () => {
      const scrollWidth = tableBody.scrollWidth;
      const clientWidth = tableBody.clientWidth;
      const overflowing = scrollWidth > clientWidth;
      
      fakeInner.style.width = `${scrollWidth}px`;
      setIsPreviewTableOverflowing(overflowing);
    };
    // Delay first sync slightly to let Ant Design finish rendering
    const initialTimer = setTimeout(syncWidth, 50);

    // Bidirectional sync
    const onTableScroll = () => {
      if (isPreviewSyncingScroll.current) return;
      isPreviewSyncingScroll.current = true;
      fakeScroll.scrollLeft = tableBody.scrollLeft;
      isPreviewSyncingScroll.current = false;
    };
    const onFakeScroll = () => {
      if (isPreviewSyncingScroll.current) return;
      isPreviewSyncingScroll.current = true;
      tableBody.scrollLeft = fakeScroll.scrollLeft;
      isPreviewSyncingScroll.current = false;
    };

    tableBody.addEventListener('scroll', onTableScroll);
    fakeScroll.addEventListener('scroll', onFakeScroll);

    // Re-sync on resize
    const resizeObserver = new ResizeObserver(syncWidth);
    resizeObserver.observe(tableBody);

    return () => {
      clearTimeout(initialTimer);
      tableBody.removeEventListener('scroll', onTableScroll);
      fakeScroll.removeEventListener('scroll', onFakeScroll);
      resizeObserver.disconnect();
    };
  }, [showLeadPreview, filteredLeads]); // re-run when preview visibility or data changes

  // Count of active exclude filters for the badge
  const excludeFilterCount = [
    excludeTherapy, excludeLeadSource, excludeLeadPotential, excludeAddedBy,
    excludeCountry, excludeCity, excludeRole, excludeDomain, excludeTags,
  ].filter(v => v && v.length > 0).length;

  const moreFilterCount = [
    filterRole, filterDomain, filterTags,
  ].filter(v => v && v.length > 0).length
    + (filterMinEmailsSent !== undefined ? 1 : 0)
    + (filterMaxEmailsSent !== undefined ? 1 : 0)
    + excludeFilterCount;

  // Compute effective lead count (after manual deselections)
  // Since we paginate the preview, we track deselected IDs (small set) rather than selected IDs
  const effectiveLeadCount = manuallyDeselectedIds.size > 0
    ? Math.max(0, filteredLeadsTotal - manuallyDeselectedIds.size)
    : filteredLeadsTotal;

  useEffect(() => {
    outreachCampaignsApi.list({ page: 1, page_size: 100 })
      .then(res => {
        const active = res.items.filter(c => c.is_active);
        setCampaigns(active);
      })
      .catch(() => message.error('Failed to fetch campaigns'));

    outreachSenderAccountsApi.list()
      .then(res => setSenderAccounts(res.items))
      .catch(() => message.error('Failed to fetch sender accounts'));

    fetchScheduledSends();
  }, []);

  const fetchScheduledSends = async () => {
    setScheduledSendsLoading(true);
    try {
      const res = await outreachSendsApi.listScheduledSends();
      setScheduledSends(res.items);
    } catch {
      // Silently fail if table doesn't exist yet
    } finally {
      setScheduledSendsLoading(false);
    }
  };

  const fetchSendJobs = useCallback(async (showLoading = false) => {
    if (showLoading) setSendJobsLoading(true);
    try {
      const res = await outreachSendsApi.listJobs();
      setSendJobs(res.jobs);
    } catch {
      // Silently fail
    } finally {
      if (showLoading) setSendJobsLoading(false);
    }
  }, []);

  // Poll jobs + scheduled sends when on Jobs tab (refresh every 3s for live progress)
  useEffect(() => {
    if (activeTab === 'jobs') {
      fetchSendJobs(true);
      fetchScheduledSends();
      jobsPollingRef.current = setInterval(() => {
        fetchSendJobs(false);
        fetchScheduledSends();
      }, 3000);
    } else {
      if (jobsPollingRef.current) {
        clearInterval(jobsPollingRef.current);
        jobsPollingRef.current = null;
      }
    }
    return () => {
      if (jobsPollingRef.current) {
        clearInterval(jobsPollingRef.current);
        jobsPollingRef.current = null;
      }
    };
  }, [activeTab, fetchSendJobs]);

  const handleCancelJob = async (jobId: string) => {
    try {
      await outreachSendsApi.cancelSend(jobId);
      message.info('Cancelling job...');
      fetchSendJobs(false);
    } catch (e: any) {
      message.error(e.response?.data?.detail || 'Failed to cancel job');
    }
  };

  const fetchPreview = useCallback(async (campaignId: string, senderAccountId: string) => {
    if (!campaignId || !senderAccountId) { setPreviewData(null); return; }
    setPreviewLoading(true);
    try {
      const result = await outreachSendsApi.preview({ campaign_id: campaignId, sender_account_id: senderAccountId });
      setPreviewData(result);
    } catch {
      setPreviewData(null);
    } finally {
      setPreviewLoading(false);
    }
  }, []);

  const handleCampaignChange = (campaignId: string) => {
    setSelectedCampaignId(campaignId);
    setDryRunResult(null);
    setTestSendResult(null);
    setLiveSendResult(null);
    if (selectedSenderAccountId) fetchPreview(campaignId, selectedSenderAccountId);
  };

  const handleSenderAccountChange = (accountId: string) => {
    setSelectedSenderAccountId(accountId);
    if (selectedCampaignId) fetchPreview(selectedCampaignId, accountId);
  };

  const handleDryRun = async () => {
    if (!selectedCampaignId) { message.warning('Select a campaign'); return; }
    try {
      const result = await outreachSendsApi.dryRun({
        campaign_id: selectedCampaignId,
        lead_filters: getActiveLeadFilters(),
        exclude_lead_ids: getExcludeLeadIds(),
      });
      setDryRunResult(result); message.success('Dry run completed');
    } catch (e: any) { message.error(e.response?.data?.detail || 'Dry run failed'); }
  };

  const handleOpenTestModal = () => {
    setTestModalVisible(true);
    if (selectedCampaignId && selectedSenderAccountId) fetchPreview(selectedCampaignId, selectedSenderAccountId);
  };

  const handleTestSendClick = async () => {
    if (!selectedCampaignId) { message.warning('Select a campaign'); return; }
    if (!selectedSenderAccountId) { message.warning('Select a sender account'); return; }
    const emails = testEmails.split('\n').map(e => e.trim()).filter(Boolean);
    if (!emails.length) { message.warning('Enter at least one email'); return; }
    if (emails.length > 10) { message.warning('Max 10 emails'); return; }

    const hideLoading = message.loading('Sending test email...', 0);
    setTestSendLoading(true);
    try {
      const result = await outreachSendsApi.testSend({
        campaign_id: selectedCampaignId,
        sender_account_id: selectedSenderAccountId,
        recipient_emails: emails,
      });
      setTestSendResult(result);
      setTestModalVisible(false);
      setTestEmails('');
      message.success(`Test: ${result.sent_count} sent, ${result.failed_count} failed`);
    } catch (e: any) {
      const errorDetail = e.response?.data?.detail || e.message || 'Test send failed';
      message.error(errorDetail, 5);
      console.error('Test send error:', e);
    } finally {
      hideLoading();
      setTestSendLoading(false);
    }
  };

  // -- Polling logic for live send progress --
  const startPolling = useCallback((jobId: string) => {
    if (pollingRef.current) clearInterval(pollingRef.current);
    pollingRef.current = setInterval(async () => {
      try {
        const status = await outreachSendsApi.sendStatus(jobId);
        setSendProgress(status);
        if (status.status === 'completed' || status.status === 'failed' || status.status === 'cancelled') {
          if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; }
          if (status.status === 'completed') {
            setLiveSendResult({
              sent_count: status.sent_count,
              failed_count: status.failed_count,
              skipped_count: status.skipped_count,
              results: status.results || [],
            });
            message.success(`Send complete: ${status.sent_count} sent, ${status.failed_count} failed`);
          } else if (status.status === 'cancelled') {
            message.info(`Send cancelled: ${status.sent_count} sent before stopping`);
          } else {
            message.error(`Send failed: ${status.error || 'Unknown error'}`);
          }
        }
      } catch {
        // If poll fails, keep trying
      }
    }, 2000);
  }, []);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => { if (pollingRef.current) clearInterval(pollingRef.current); };
  }, []);

  const handleLiveSend = async () => {
    if (!selectedCampaignId) { message.warning('Select a campaign'); return; }
    if (!selectedSenderAccountId) { message.warning('Select a sender account'); return; }
    // Prevent double-click: if already in progress, silently ignore
    if (liveSendLoading) return;
    
    setLiveSendLoading(true);
    const hideLoading = message.loading('Starting send job...', 0);
    try {
      console.log('Making live send API call...');
      const result = await outreachSendsApi.liveSend({
        campaign_id: selectedCampaignId,
        sender_account_id: selectedSenderAccountId,
        lead_filters: getActiveLeadFilters(),
        exclude_lead_ids: getExcludeLeadIds(),
      });
      console.log('Live send result:', result);
      
      if (result && result.job_id) {
        setSendJobId(result.job_id);
        setSendProgress(null);
        setProgressVisible(true);
        setProgressMinimized(false);
        startPolling(result.job_id);
        message.success('Send job started successfully');
      } else {
        console.error('Invalid response from live send API:', result);
        message.error('Invalid response from server');
      }
    } catch (e: any) { 
      console.error('Live send error:', e);
      const errorMsg = e.response?.data?.detail || e.message || 'Failed to start send';
      message.error(errorMsg, 10); 
    } finally {
      hideLoading();
      setLiveSendLoading(false);
    }
  };

  const handleCancelLiveSend = async () => {
    if (!sendJobId) return;
    try {
      await outreachSendsApi.cancelSend(sendJobId);
      message.info('Cancelling send...');
    } catch (e: any) {
      message.error(e.response?.data?.detail || 'Failed to cancel');
    }
  };

  const handleOpenScheduleModal = () => {
    if (!selectedCampaignId) { message.warning('Select a campaign'); return; }
    if (!selectedSenderAccountId) { message.warning('Select a sender account'); return; }
    setScheduleModalVisible(true);
    setScheduledDateTime(null);
    setScheduleMaxSends(undefined);
  };

  const handleScheduleSend = async () => {
    if (!scheduledDateTime) { message.warning('Select a date and time'); return; }
    if (scheduledDateTime.isBefore(dayjs())) { message.warning('Scheduled time must be in the future'); return; }

    setScheduleLoading(true);
    try {
      await outreachSendsApi.scheduleSend({
        campaign_id: selectedCampaignId,
        sender_account_id: selectedSenderAccountId,
        scheduled_at: scheduledDateTime.utc().toISOString(),
        max_sends: scheduleMaxSends,
        lead_filters: getActiveLeadFilters(),
        exclude_lead_ids: getExcludeLeadIds(),
      });
      const scheduledForText = scheduledDateTime.format('MMM D, YYYY h:mm A');
      setScheduleModalVisible(false);
      fetchScheduledSends();

      // Immediate toast so the user always sees some confirmation even if the
      // modal portal gets blocked for any reason.
      message.success(`Scheduled for ${scheduledForText} — added to queue`, 4);

      // Confirmation popup: it's been queued; point them at the Jobs tab.
      // Defer to the next tick so React finishes closing the schedule modal
      // before the confirmation portal mounts (avoids v5 focus-trap races).
      setTimeout(() => {
        Modal.confirm({
          title: 'Scheduled send added to queue',
          icon: <CheckCircleOutlined style={{ color: '#52c41a' }} />,
          content: (
            <div>
              <p style={{ marginBottom: 8 }}>
                This send will run automatically on <Text strong>{scheduledForText}</Text>.
              </p>
              <p style={{ marginBottom: 0, color: '#8c8c8c', fontSize: 13 }}>
                You can track progress or cancel it from the <Text strong>Jobs</Text> tab.
              </p>
            </div>
          ),
          okText: 'View in Jobs',
          cancelText: 'Close',
          centered: true,
          onOk: () => setActiveTab('jobs'),
        });
      }, 0);
    } catch (e: any) {
      message.error(e.response?.data?.detail || 'Failed to schedule send');
    } finally {
      setScheduleLoading(false);
    }
  };

  const handleCancelScheduledSend = async (sendId: string) => {
    try {
      await outreachSendsApi.cancelScheduledSend(sendId);
      message.success('Scheduled send cancelled');
      fetchScheduledSends();
    } catch (e: any) {
      message.error(e.response?.data?.detail || 'Failed to cancel');
    }
  };

  const selected = campaigns.find(c => c.id === selectedCampaignId);
  const selectedAccount = senderAccounts.find(a => a.id === selectedSenderAccountId);
  const notReady = !selectedCampaignId || !selectedSenderAccountId;
  // True when a send job is currently in progress (running or starting)
  const isSendJobActive = liveSendLoading || (progressVisible && sendProgress?.status === 'running');

  // Daily limit awareness
  const remainingDailyQuota = selectedAccount
    ? Math.max(0, selectedAccount.daily_send_limit - selectedAccount.current_daily_sends)
    : Infinity;
  const leadsExceedLimit = effectiveLeadCount > remainingDailyQuota && remainingDailyQuota !== Infinity;

  // Get the small set of manually-deselected IDs to send as exclude_lead_ids
  const getExcludeLeadIds = useCallback((): string[] | undefined => {
    if (manuallyDeselectedIds.size > 0) {
      return Array.from(manuallyDeselectedIds);
    }
    return undefined;
  }, [manuallyDeselectedIds]);

  // Column resize handler for preview table
  const handlePreviewColumnResizeStart = useCallback((columnKey: PreviewColumnKey, event: React.MouseEvent<HTMLSpanElement>) => {
    event.preventDefault();
    event.stopPropagation();

    const startX = event.clientX;
    const startWidth = previewColumnWidths[columnKey];
    let lastWidth = startWidth;
    let frameId: number | null = null;

    const handleMouseMove = (moveEvent: MouseEvent) => {
      const delta = moveEvent.clientX - startX;
      const nextWidth = Math.max(60, startWidth + delta);

      if (nextWidth !== lastWidth) {
        lastWidth = nextWidth;
        if (frameId) cancelAnimationFrame(frameId);
        frameId = requestAnimationFrame(() => {
          setPreviewColumnWidths(prev => ({ ...prev, [columnKey]: nextWidth }));
        });
      }
    };

    const handleMouseUp = () => {
      if (frameId) cancelAnimationFrame(frameId);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [previewColumnWidths]);

  const getPreviewResizableHeaderProps = useCallback((columnKey: PreviewColumnKey): ResizableHeaderCellProps => ({
    width: previewColumnWidths[columnKey],
    onResizeStart: (event: React.MouseEvent<HTMLSpanElement>) => handlePreviewColumnResizeStart(columnKey, event),
  }), [previewColumnWidths, handlePreviewColumnResizeStart]);

  // Select all / Deselect all leads across ALL pages
  const handleSelectAllLeads = useCallback(() => {
    setManuallyDeselectedIds(new Set());
  }, []);

  const [deselectAllLoading, setDeselectAllLoading] = useState(false);

  const handleDeselectAllLeads = useCallback(async () => {
    setDeselectAllLoading(true);
    try {
      // Fetch all matching lead IDs in batches
      const allIds = new Set<string>();
      let page = 1;
      const batchSize = 500;
      let totalPages = 1;
      const filterParams = getActiveLeadFilters() || {};

      do {
        const res = await outreachLeadsApi.list({
          page,
          page_size: batchSize,
          ...filterParams,
        });
        res.items.forEach(lead => allIds.add(lead.id));
        totalPages = res.total_pages;
        page++;
      } while (page <= totalPages);

      setManuallyDeselectedIds(allIds);
    } catch {
      message.error('Failed to deselect all leads');
    } finally {
      setDeselectAllLoading(false);
    }
  }, [getActiveLeadFilters]);

  // Memoize preview table columns so they aren't re-created on every render
  const previewColumns = useMemo(() => [
    {
      title: 'Email', dataIndex: 'email', key: 'email',
      width: previewColumnWidths.email, ellipsis: true,
      onHeaderCell: () => getPreviewResizableHeaderProps('email'),
    },
    {
      title: 'Name', key: 'name',
      width: previewColumnWidths.name, ellipsis: true,
      onHeaderCell: () => getPreviewResizableHeaderProps('name'),
      render: (_: any, r: Lead) => [r.first_name, r.last_name].filter(Boolean).join(' ') || '-',
    },
    {
      title: 'Company', dataIndex: 'company', key: 'company',
      width: previewColumnWidths.company, ellipsis: true,
      onHeaderCell: () => getPreviewResizableHeaderProps('company'),
      render: (v: string | null) => v || '-',
    },
    {
      title: 'Role', dataIndex: 'role', key: 'role',
      width: previewColumnWidths.role, ellipsis: true,
      onHeaderCell: () => getPreviewResizableHeaderProps('role'),
      render: (v: string | null) => v || '-',
    },
    {
      title: 'Domain', dataIndex: 'domain', key: 'domain',
      width: previewColumnWidths.domain, ellipsis: true,
      onHeaderCell: () => getPreviewResizableHeaderProps('domain'),
      render: (v: string | null) => v || '-',
    },
    {
      title: 'Therapy', dataIndex: 'therapy_interest', key: 'therapy',
      width: previewColumnWidths.therapy,
      onHeaderCell: () => getPreviewResizableHeaderProps('therapy'),
      render: (vals: string[] | null) => !vals?.length ? '-' : (
        <div style={{ whiteSpace: 'nowrap' }}>
          <Space size={4}>{vals.map(v => <Tag key={v} color="geekblue">{v}</Tag>)}</Space>
        </div>
      ),
    },
    {
      title: 'Status', key: 'status',
      width: previewColumnWidths.status,
      onHeaderCell: () => getPreviewResizableHeaderProps('status'),
      render: (_: any, r: Lead) => renderLeadStatusTag(r),
    },
    {
      title: 'Last Contacted', dataIndex: 'last_contacted_at', key: 'last_contacted_at',
      width: previewColumnWidths.last_contacted_at,
      onHeaderCell: () => getPreviewResizableHeaderProps('last_contacted_at'),
      render: (value: string | null) => {
        if (!value) return '-';
        const d = dayjs(value);
        const diff = dayjs().startOf('day').diff(d.startOf('day'), 'day');
        const diffText = diff === 0 ? 'Today' : `${diff}d ago`;
        return (
          <span>
            {d.format('DD-MM-YYYY')}
            <Typography.Text type="secondary" style={{ fontSize: 11, marginLeft: 4 }}>
              ({diffText})
            </Typography.Text>
          </span>
        );
      },
    },
    {
      title: 'Country', dataIndex: 'country', key: 'country',
      width: previewColumnWidths.country,
      onHeaderCell: () => getPreviewResizableHeaderProps('country'),
      render: (v: string | null) => v || '-',
    },
    {
      title: 'City', dataIndex: 'city', key: 'city',
      width: previewColumnWidths.city,
      onHeaderCell: () => getPreviewResizableHeaderProps('city'),
      render: (v: string | null) => v || '-',
    },
    {
      title: 'Source', dataIndex: 'lead_source', key: 'source',
      width: previewColumnWidths.source,
      onHeaderCell: () => getPreviewResizableHeaderProps('source'),
      render: (v: string | null) => v ? <Tag>{v}</Tag> : '-',
    },
    {
      title: 'Potential', dataIndex: 'lead_potential', key: 'potential',
      width: previewColumnWidths.potential,
      onHeaderCell: () => getPreviewResizableHeaderProps('potential'),
      render: (v: string | null) => v
        ? <Tag color={LEAD_POTENTIAL_COLORS[v] || 'default'}>{v}</Tag>
        : '-',
    },
    {
      title: 'Added By', dataIndex: 'added_by', key: 'added_by',
      width: previewColumnWidths.added_by,
      onHeaderCell: () => getPreviewResizableHeaderProps('added_by'),
      render: (v: string | null) => v || '-',
    },
    {
      title: 'Sent', dataIndex: 'total_emails_sent', key: 'sent',
      width: previewColumnWidths.sent,
      onHeaderCell: () => getPreviewResizableHeaderProps('sent'),
      render: (v: number) => v || 0,
    },
    {
      title: 'Next Follow-up', dataIndex: 'next_followup_at', key: 'next_followup_at',
      width: previewColumnWidths.next_followup_at,
      onHeaderCell: () => getPreviewResizableHeaderProps('next_followup_at'),
      render: (value: string | null) => {
        if (!value) return '-';
        const d = dayjs(value);
        const isPast = d.isBefore(dayjs());
        return <Typography.Text type={isPast ? 'danger' : undefined}>{d.format('YYYY-MM-DD')}</Typography.Text>;
      },
    },
    {
      title: 'Tags', dataIndex: 'tags', key: 'tags',
      width: previewColumnWidths.tags,
      onHeaderCell: () => getPreviewResizableHeaderProps('tags'),
      render: (tags: string[] | null) => !tags?.length ? '-' : (
        <div style={{ whiteSpace: 'nowrap' }}>
          <Space size={4}>{tags.map(tag => <Tag key={tag}>{tag}</Tag>)}</Space>
        </div>
      ),
    },
  ], [previewColumnWidths, getPreviewResizableHeaderProps]);

  const statusColor: Record<string, string> = {
    pending: 'blue', processing: 'orange', completed: 'green', failed: 'red', cancelled: 'default',
  };

  const scheduledColumns = [
    {
      title: 'Campaign', dataIndex: 'campaign_id', key: 'campaign',
      render: (id: string) => campaigns.find(c => c.id === id)?.name || id.substring(0, 8),
    },
    {
      title: 'Sender', dataIndex: 'sender_account_id', key: 'sender',
      render: (id: string) => {
        const acct = senderAccounts.find(a => a.id === id);
        return acct ? `${acct.display_name || acct.account_key}` : id.substring(0, 8);
      },
    },
    {
      title: 'Scheduled For', dataIndex: 'scheduled_at', key: 'scheduled_at',
      render: (v: string) => dayjs(v).format('MMM D, YYYY h:mm A'),
    },
    { title: 'Max Sends', dataIndex: 'max_sends', key: 'max_sends', render: (v: number | null) => v ?? 'No limit' },
    {
      title: 'Status', dataIndex: 'status', key: 'status',
      render: (s: string) => <Tag color={statusColor[s] || 'default'}>{s.toUpperCase()}</Tag>,
    },
    {
      title: 'Result', key: 'result',
      render: (_: any, r: ScheduledSend) => {
        if (r.status === 'completed' && r.result) {
          return <Text type="success">Sent: {r.result.sent_count}, Failed: {r.result.failed_count}</Text>;
        }
        if (r.status === 'failed' && r.error_message) {
          return <Text type="danger">{r.error_message.substring(0, 50)}</Text>;
        }
        return '-';
      },
    },
    {
      title: 'Actions', key: 'actions',
      render: (_: any, r: ScheduledSend) => r.status === 'pending' ? (
        <Popconfirm title="Cancel this scheduled send?" onConfirm={() => handleCancelScheduledSend(r.id)}>
          <Button type="link" danger icon={<DeleteOutlined />} size="small">Cancel</Button>
        </Popconfirm>
      ) : null,
    },
  ];

  // Count of running jobs for the badge on the Jobs tab
  const runningJobsCount = sendJobs.filter(j => j.status === 'running' || j.status === 'cancelling').length;

  return (
    <div>
      <div style={{ 
        marginBottom: 16,
      }}>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          type="card"
          size="large"
          style={{
            marginBottom: 0,
          }}
          className="outreach-send-tabs"
          items={[
            {
              key: 'send',
              label: (
                <Space size={8}>
                  <SendOutlined />
                  <span>Send</span>
                </Space>
              ),
              children: null,
            },
            {
              key: 'jobs',
              label: (
                <Space size={8}>
                  <UnorderedListOutlined />
                  <span>Jobs</span>
                  {runningJobsCount > 0 && (
                    <Badge count={runningJobsCount} size="small" style={{ backgroundColor: '#1890ff' }} />
                  )}
                </Space>
              ),
              children: null,
            },
          ]}
        />
        <style>{`
          .outreach-send-tabs .ant-tabs-tab {
            padding: 12px 24px !important;
            font-size: 16px !important;
            font-weight: 500 !important;
            height: auto !important;
            line-height: 1.5 !important;
          }
          .outreach-send-tabs .ant-tabs-tab-active {
            font-weight: 700 !important;
          }
          .outreach-send-tabs .ant-tabs-nav {
            margin-bottom: 0 !important;
          }
          .outreach-send-tabs .ant-tabs-tab + .ant-tabs-tab {
            margin-left: 0 !important;
          }
        `}</style>
      </div>

      {activeTab === 'send' && (
      <>
      <Card style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.06)', marginBottom: 24 }}>
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <div>
            <Text strong>Select Campaign</Text>
            <Select style={{ width: '100%', marginTop: 8 }} placeholder="Choose a campaign"
              value={selectedCampaignId || undefined} onChange={handleCampaignChange}>
              {campaigns.map(c => <Select.Option key={c.id} value={c.id}>{c.name} ({c.campaign_id})</Select.Option>)}
            </Select>
          </div>
          {selected && (
            <div style={{ padding: 12, background: isDark ? '#1f1f1f' : '#f5f7fa', borderRadius: 6, border: isDark ? '1px solid #434343' : 'none' }}>
              <div><Text>Subject: </Text><Text strong>{selected.subject}</Text></div>
              <div style={{ marginTop: 4 }}><Text>Brand: </Text><Tag color={selected.brand === 'sns_intel' ? 'blue' : 'purple'}>{selected.brand}</Tag></div>
            </div>
          )}
          <div style={{ opacity: !selectedCampaignId ? 0.4 : 1, pointerEvents: !selectedCampaignId ? 'none' : 'auto' }}>
            <Text strong>Select Sender Account</Text>
            <Select style={{ width: '100%', marginTop: 8 }} placeholder="Choose a sender account"
              value={selectedSenderAccountId || undefined} onChange={handleSenderAccountChange}
              disabled={!selectedCampaignId}>
              {senderAccounts.map(a => (
                <Select.Option key={a.id} value={a.id}>
                  {a.display_name || a.account_key} ({a.email})
                </Select.Option>
              ))}
            </Select>
            {selectedAccount && (
              <div style={{ marginTop: 8, padding: 8, background: isDark ? '#1f1f1f' : '#f9f9f9', borderRadius: 4, fontSize: 12, border: isDark ? '1px solid #434343' : 'none' }}>
                <Text type="secondary">
                  Sends today: {selectedAccount.current_daily_sends} / {selectedAccount.daily_send_limit}
                  {' · '}
                  <Text strong style={{ color: remainingDailyQuota === 0 ? '#ff4d4f' : remainingDailyQuota <= 10 ? '#faad14' : '#52c41a', fontSize: 12 }}>
                    {remainingDailyQuota} remaining
                  </Text>
                </Text>
                {remainingDailyQuota === 0 && (
                  <div style={{ marginTop: 4 }}>
                    <Text type="danger" style={{ fontSize: 11 }}>
                      <WarningOutlined style={{ marginRight: 4 }} />
                      Daily send limit reached. No emails can be sent until the limit resets.
                    </Text>
                  </div>
                )}
              </div>
            )}
          </div>
        </Space>
      </Card>

      {/* Lead Selection / Filtering Card */}
      <div style={{ opacity: notReady ? 0.4 : 1, pointerEvents: notReady ? 'none' : 'auto', transition: 'opacity 0.2s' }}>
        <Card
          title={<Space><FilterOutlined /><span>Lead Selection</span></Space>}
          style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.06)', marginBottom: 24 }}
          extra={
            <Space>
              {effectiveLeadCount > 0 && (
                <Tag color="blue" style={{ fontSize: 13, padding: '2px 10px' }}>
                  <TeamOutlined style={{ marginRight: 4 }} />
                  {effectiveLeadCount} lead{effectiveLeadCount !== 1 ? 's' : ''} selected
                </Tag>
              )}
              {manuallyDeselectedIds.size > 0 && (
                <Tag color="orange" style={{ fontSize: 12, padding: '2px 8px' }}>
                  {manuallyDeselectedIds.size} manually excluded
                </Tag>
              )}
            </Space>
          }
        >
          <div style={{ marginBottom: 12 }}>
            <Text type="secondary" style={{ fontSize: 13 }}>
              Filter leads below, then preview and manually select/deselect individual leads. If no filters are set, all eligible leads will be targeted.
            </Text>
          </div>

          <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '12px 16px', marginBottom: 16 }}>
                <div>
                  <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>Therapy Interest</Text>
                  <Select
                    mode="multiple"
                    placeholder="Select therapy areas"
                    style={{ width: '100%' }}
                    allowClear
                    value={filterTherapy}
                    onChange={setFilterTherapy}
                    maxTagCount="responsive"
                    loading={filterOptionsLoading}
                  >
                    {filterOptions.therapy_interest.map(o => <Select.Option key={o} value={o}>{o}</Select.Option>)}
                  </Select>
                </div>
                <div>
                  <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>Lead Potential</Text>
                  <Select
                    mode="multiple"
                    placeholder="Any potential"
                    style={{ width: '100%' }}
                    allowClear
                    value={filterLeadPotential}
                    onChange={(v) => setFilterLeadPotential(v?.length ? v : undefined)}
                    maxTagCount="responsive"
                    loading={filterOptionsLoading}
                  >
                    {filterOptions.lead_potential.map(o => <Select.Option key={o} value={o}>{o}</Select.Option>)}
                  </Select>
                </div>
                <div>
                  <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>Lead Source</Text>
                  <Select
                    mode="multiple"
                    placeholder="Any source"
                    style={{ width: '100%' }}
                    allowClear
                    value={filterLeadSource}
                    onChange={(v) => setFilterLeadSource(v?.length ? v : undefined)}
                    maxTagCount="responsive"
                    loading={filterOptionsLoading}
                  >
                    {filterOptions.lead_source.map(o => <Select.Option key={o} value={o}>{o}</Select.Option>)}
                  </Select>
                </div>
                <div>
                  <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>Added By</Text>
                  <Select
                    mode="multiple"
                    placeholder="Anyone"
                    style={{ width: '100%' }}
                    allowClear
                    value={filterAddedBy}
                    onChange={(v) => setFilterAddedBy(v?.length ? v : undefined)}
                    maxTagCount="responsive"
                    loading={filterOptionsLoading}
                  >
                    {filterOptions.added_by.map(o => <Select.Option key={o} value={o}>{o}</Select.Option>)}
                  </Select>
                </div>
                <div>
                  <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>Status</Text>
                  <Select
                    placeholder="Any status"
                    style={{ width: '100%' }}
                    allowClear
                    value={filterStatus}
                    onChange={setFilterStatus}
                  >
                    <Select.Option value="active">Active</Select.Option>
                    <Select.Option value="bounces">Bounces</Select.Option>
                    <Select.Option value="unsubscribes">Unsubscribes</Select.Option>
                  </Select>
                </div>
                <div>
                  <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>Country</Text>
                  <Select
                    mode="tags"
                    placeholder="e.g. Germany"
                    allowClear
                    value={filterCountry}
                    onChange={(v) => setFilterCountry(v?.length ? v : undefined)}
                    style={{ width: '100%' }}
                    showSearch
                    filterOption={(input, option) => {
                      const value = option?.value;
                      if (value && typeof value === 'string') {
                        return value.toLowerCase().includes(input.toLowerCase());
                      }
                      return false;
                    }}
                    loading={filterOptionsLoading}
                    notFoundContent={filterOptionsLoading ? <Spin size="small" /> : 'No matching countries'}
                  >
                    {filterOptions.countries.map(c => (
                      <Select.Option key={c} value={c}>{c}</Select.Option>
                    ))}
                  </Select>
                </div>
                <div>
                  <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>City</Text>
                  <Select
                    mode="tags"
                    placeholder="e.g. Berlin"
                    allowClear
                    value={filterCity}
                    onChange={(v) => setFilterCity(v?.length ? v : undefined)}
                    style={{ width: '100%' }}
                    showSearch
                    filterOption={(input, option) => {
                      const value = option?.value;
                      if (value && typeof value === 'string') {
                        return value.toLowerCase().includes(input.toLowerCase());
                      }
                      return false;
                    }}
                    loading={filterOptionsLoading}
                    notFoundContent={filterOptionsLoading ? <Spin size="small" /> : 'No matching cities'}
                  >
                    {filterOptions.cities.map(c => (
                      <Select.Option key={c} value={c}>{c}</Select.Option>
                    ))}
                  </Select>
                </div>
                <div>
                  <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>Last Contacted</Text>
                  <Select
                    placeholder="Any time"
                    style={{ width: '100%' }}
                    allowClear
                    value={filterLastContactedDaysAgo}
                    onChange={setFilterLastContactedDaysAgo}
                  >
                    {LAST_CONTACTED_OPTIONS.map(o => (
                      <Select.Option key={o.value} value={o.value}>{o.label}</Select.Option>
                    ))}
                  </Select>
                </div>
                <div>
                  <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>Search</Text>
                  <Input
                    placeholder="Email, name, company..."
                    prefix={<SearchOutlined />}
                    allowClear
                    value={filterSearch}
                    onChange={e => setFilterSearch(e.target.value || undefined)}
                  />
                </div>
              </div>

              <Space style={{ marginBottom: 16 }}>
                <Button
                  icon={<FilterOutlined />}
                  onClick={() => setFilterDrawerVisible(true)}
                >
                  More Filters
                  {moreFilterCount > 0 && (
                    <Badge count={moreFilterCount} size="small" style={{ marginLeft: 6, backgroundColor: '#1890ff' }} />
                  )}
                </Button>
                <Button
                  type="link"
                  icon={<ReloadOutlined />}
                  onClick={resetLeadFilters}
                  style={{ padding: 0 }}
                >
                  Reset All Filters
                </Button>
              </Space>

              {/* Filter results summary */}
              {filteredLeadsLoading ? (
                <div style={{ padding: '12px 0' }}>
                  <Spin size="small" /> <Text type="secondary" style={{ marginLeft: 8 }}>Counting matching leads...</Text>
                </div>
              ) : (
                <div style={{
                  padding: '12px 16px',
                  background: effectiveLeadCount > 0 
                    ? (isDark ? 'rgba(24, 144, 255, 0.1)' : '#f0f5ff')
                    : (isDark ? 'rgba(250, 173, 20, 0.1)' : '#fff7e6'),
                  border: effectiveLeadCount > 0 
                    ? (isDark ? '1px solid rgba(24, 144, 255, 0.3)' : '1px solid #d6e4ff')
                    : (isDark ? '1px solid rgba(250, 173, 20, 0.3)' : '1px solid #ffe58f'),
                  borderRadius: 8,
                  marginBottom: 12,
                }}>
                  <Space>
                    <TeamOutlined style={{ color: effectiveLeadCount > 0 ? '#1890ff' : '#faad14', fontSize: 16 }} />
                    <Text strong style={{ fontSize: 14 }}>
                      {manuallyDeselectedIds.size > 0 ? (
                        <>
                          {effectiveLeadCount} of {filteredLeadsTotal} lead{filteredLeadsTotal !== 1 ? 's' : ''} selected for sending
                        </>
                      ) : (
                        <>
                          {filteredLeadsTotal} lead{filteredLeadsTotal !== 1 ? 's' : ''} match your filters
                        </>
                      )}
                    </Text>
                    {!getActiveLeadFilters() && !showLeadPreview && (
                      <Text type="secondary" style={{ fontSize: 12 }}>(no filters set – will target all leads)</Text>
                    )}
                  </Space>
                  <div style={{ marginTop: 4 }}>
                    <Button
                      type="link"
                      size="small"
                      icon={<EyeOutlined />}
                      onClick={() => {
                        setShowLeadPreview(!showLeadPreview);
                        if (!showLeadPreview) fetchFilteredLeads(1);
                      }}
                      style={{ padding: 0 }}
                    >
                      {showLeadPreview ? 'Hide' : 'Preview & Select'} leads
                    </Button>
                    {showLeadPreview && manuallyDeselectedIds.size > 0 && (
                      <>
                        <Divider type="vertical" />
                        <Button
                          type="link"
                          size="small"
                          onClick={() => setManuallyDeselectedIds(new Set())}
                          style={{ padding: 0 }}
                        >
                          Clear Exclusions ({manuallyDeselectedIds.size})
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Lead preview table with checkboxes */}
              {showLeadPreview && (
                <div style={{ marginTop: 8 }}>
                  <style>{`
                    /* Hide the original preview table horizontal scrollbar */
                    .preview-table-wrapper .ant-table-body::-webkit-scrollbar,
                    .preview-table-wrapper .ant-table-content::-webkit-scrollbar,
                    .preview-table-wrapper .ant-table-header::-webkit-scrollbar {
                      height: 0px !important;
                      display: none !important;
                    }
                    .preview-table-wrapper .ant-table-body,
                    .preview-table-wrapper .ant-table-content,
                    .preview-table-wrapper .ant-table-header {
                      scrollbar-width: none !important;  /* Firefox */
                      -ms-overflow-style: none !important; /* IE/Edge */
                    }
                    .preview-sticky-scrollbar-top {
                      position: sticky;
                      top: 0;
                      z-index: 1001; /* Must be above sticky table header */
                      overflow-x: auto;
                      overflow-y: hidden;
                      height: 12px;
                      background: ${isDark ? '#1f1f1f' : '#fafafa'};
                      border-bottom: 1px solid ${isDark ? '#434343' : '#f0f0f0'};
                      display: ${isPreviewTableOverflowing ? 'block' : 'none'};
                    }
                    .preview-sticky-scrollbar-top::-webkit-scrollbar {
                      height: 8px;
                    }
                    .preview-sticky-scrollbar-top::-webkit-scrollbar-thumb {
                      background: ${isDark ? '#555' : '#c1c1c1'};
                      border-radius: 4px;
                      border: 1px solid ${isDark ? '#1f1f1f' : '#fafafa'};
                    }
                    .preview-sticky-scrollbar-top::-webkit-scrollbar-thumb:hover {
                      background: ${isDark ? '#777' : '#999'};
                    }
                    .preview-sticky-scrollbar-top::-webkit-scrollbar-track {
                      background: ${isDark ? '#1f1f1f' : '#fafafa'};
                    }
                    /* Hide Ant Design's built-in sticky scrollbar */
                    .preview-table-wrapper .ant-table-sticky-scrollbar {
                      display: none !important;
                    }
                    /* Column resize handle styles */
                    .preview-table-wrapper .column-resize-handle:hover .handle-bar {
                      background: #1890ff !important;
                      width: 3px !important;
                    }
                    .preview-table-wrapper .column-resize-handle:hover {
                      background: rgba(24, 144, 255, 0.1) !important;
                    }
                    .preview-table-wrapper .column-resize-handle.is-resizing .handle-bar {
                      background: #1890ff !important;
                      width: 3px !important;
                    }
                    .preview-table-wrapper .column-resize-handle.is-resizing {
                      background: rgba(24, 144, 255, 0.15) !important;
                    }
                    .preview-table-wrapper .ant-table-thead > tr > th {
                      position: relative;
                    }
                    .preview-table-wrapper .ant-table-thead > tr > th::after {
                      content: "";
                      position: absolute;
                      right: 0;
                      top: 25%;
                      height: 50%;
                      width: 1px;
                      background-color: ${isDark ? '#434343' : '#f0f0f0'};
                    }
                  `}</style>

                  {/* Top toolbar: select/deselect all + pagination */}
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 8,
                    flexWrap: 'wrap',
                    gap: 8,
                  }}>
                    <Space size={8}>
                      <Button
                        size="small"
                        icon={<CheckSquareOutlined />}
                        onClick={handleSelectAllLeads}
                        disabled={manuallyDeselectedIds.size === 0}
                      >
                        Select All ({filteredLeadsTotal})
                      </Button>
                      <Button
                        size="small"
                        icon={<MinusSquareOutlined />}
                        onClick={handleDeselectAllLeads}
                        loading={deselectAllLoading}
                        disabled={effectiveLeadCount === 0}
                      >
                        Deselect All
                      </Button>
                    </Space>
                    {filteredLeadsTotal > PREVIEW_PAGE_SIZE && (
                      <Pagination
                        current={previewPage}
                        pageSize={PREVIEW_PAGE_SIZE}
                        total={filteredLeadsTotal}
                        showSizeChanger={false}
                        showTotal={(total, range) => `${range[0]}-${range[1]} of ${total}`}
                        onChange={(page) => fetchFilteredLeads(page)}
                        size="small"
                      />
                    )}
                  </div>

                  {/* Sticky horizontal scrollbar below toolbar */}
                  <div
                    ref={previewFakeScrollRef}
                    className="preview-sticky-scrollbar-top"
                  >
                    <div ref={previewFakeScrollInnerRef} style={{ height: 1 }} />
                  </div>

                  <div ref={previewTableWrapperRef} className="preview-table-wrapper">
                    <Table
                      dataSource={filteredLeads}
                      loading={filteredLeadsLoading}
                      rowKey="id"
                      size="small"
                      scroll={{ x: 2000 }}
                      sticky={{
                        offsetHeader: isPreviewTableOverflowing ? 12 : 0,
                      }}
                      components={{ header: { cell: ResizableHeaderCell } }}
                      pagination={{
                        current: previewPage,
                        pageSize: PREVIEW_PAGE_SIZE,
                        total: filteredLeadsTotal,
                        showSizeChanger: false,
                        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} leads`,
                        onChange: (page) => fetchFilteredLeads(page),
                        size: 'small',
                      }}
                      rowSelection={{
                        // Rows are selected unless they are in the deselected set
                        selectedRowKeys: filteredLeads
                          .map(l => l.id)
                          .filter(id => !manuallyDeselectedIds.has(id)),
                        onChange: (_newKeys: React.Key[]) => {
                          // Compute which leads on this page were toggled
                          const pageIds = new Set(filteredLeads.map(l => l.id));
                          const newSelected = new Set(_newKeys.map(k => String(k)));
                          setManuallyDeselectedIds(prev => {
                            const next = new Set(prev);
                            pageIds.forEach(id => {
                              if (newSelected.has(id)) {
                                next.delete(id);   // re-selected
                              } else {
                                next.add(id);       // deselected
                              }
                            });
                            return next;
                          });
                        },
                        columnWidth: 48,
                      }}
                      columns={previewColumns}
                    />
                  </div>
                  {!filteredLeadsLoading && (
                    <div style={{ marginTop: 4, display: 'flex', justifyContent: 'flex-end' }}>
                      <Button
                        size="small"
                        icon={<ReloadOutlined />}
                        onClick={() => fetchFilteredLeads(previewPage)}
                        loading={filteredLeadsLoading}
                      >
                        Refresh
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </>
        </Card>
      </div>

      {/* More Filters Drawer */}
      <Drawer
        title="More Filters"
        placement="right"
        onClose={() => setFilterDrawerVisible(false)}
        open={filterDrawerVisible}
        width={420}
      >
        <Form layout="vertical">
          <Form.Item label="Role">
            <Select 
              mode="tags" 
              placeholder="Type role(s) to filter" 
              allowClear
              value={filterRole} 
              onChange={(v) => setFilterRole(v?.length ? v : undefined)}
              style={{ width: '100%' }}
              showSearch
              filterOption={(input, option) => {
                const value = option?.value;
                if (value && typeof value === 'string') {
                  return value.toLowerCase().includes(input.toLowerCase());
                }
                return false;
              }}
              loading={filterOptionsLoading}
              notFoundContent={filterOptionsLoading ? <Spin size="small" /> : 'No matching roles'}
            >
              {filterOptions.roles.map(role => (
                <Select.Option key={role} value={role}>{role}</Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item label="Domain">
            <Select 
              mode="tags" 
              placeholder="Type domain(s) to filter" 
              allowClear
              value={filterDomain} 
              onChange={(v) => setFilterDomain(v?.length ? v : undefined)}
              style={{ width: '100%' }}
              showSearch
              filterOption={(input, option) => {
                const value = option?.value;
                if (value && typeof value === 'string') {
                  return value.toLowerCase().includes(input.toLowerCase());
                }
                return false;
              }}
              loading={filterOptionsLoading}
              notFoundContent={filterOptionsLoading ? <Spin size="small" /> : 'No matching domains'}
            >
              {filterOptions.domains.map(domain => (
                <Select.Option key={domain} value={domain}>{domain}</Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item label="Tags">
            <Select 
              mode="tags" 
              placeholder="Type tag(s) to filter" 
              allowClear
              value={filterTags} 
              onChange={(v) => setFilterTags(v?.length ? v : undefined)}
              style={{ width: '100%' }}
              showSearch
              filterOption={(input, option) => {
                const value = option?.value;
                if (value && typeof value === 'string') {
                  return value.toLowerCase().includes(input.toLowerCase());
                }
                return false;
              }}
              loading={filterOptionsLoading}
              notFoundContent={filterOptionsLoading ? <Spin size="small" /> : 'No matching tags'}
            >
              {filterOptions.tags.map(tag => (
                <Select.Option key={tag} value={tag}>{tag}</Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item label="Emails Sent Range">
            <Space>
              <InputNumber
                placeholder="Min"
                min={0}
                value={filterMinEmailsSent}
                onChange={(v) => setFilterMinEmailsSent(v ?? undefined)}
                style={{ width: '100%' }}
              />
              <Text type="secondary">to</Text>
              <InputNumber
                placeholder="Max"
                min={0}
                value={filterMaxEmailsSent}
                onChange={(v) => setFilterMaxEmailsSent(v ?? undefined)}
                style={{ width: '100%' }}
              />
            </Space>
          </Form.Item>

          <div style={{ borderTop: isDark ? '1px solid #434343' : '1px solid #f0f0f0', marginTop: 16, paddingTop: 16 }}>
            <Text strong style={{ display: 'block', marginBottom: 12, fontSize: 14 }}>
              <ExclamationCircleOutlined style={{ marginRight: 6, color: '#faad14' }} />
              Exclude Filters
            </Text>
            <Text type="secondary" style={{ display: 'block', marginBottom: 12, fontSize: 12 }}>
              Leads matching these values will be excluded from results.
            </Text>

            <Form.Item label="Exclude Therapy Interest">
              <Select mode="multiple" placeholder="Exclude therapy areas" allowClear
                value={excludeTherapy} onChange={(v) => setExcludeTherapy(v?.length ? v : undefined)}
                style={{ width: '100%' }}
                loading={filterOptionsLoading}
              >
                {filterOptions.therapy_interest.map(o => <Select.Option key={o} value={o}>{o}</Select.Option>)}
              </Select>
            </Form.Item>

            <Form.Item label="Exclude Source">
              <Select mode="multiple" placeholder="Exclude source(s)" allowClear
                value={excludeLeadSource} onChange={(v) => setExcludeLeadSource(v?.length ? v : undefined)}
                style={{ width: '100%' }}
                loading={filterOptionsLoading}
              >
                {filterOptions.lead_source.map(o => <Select.Option key={o} value={o}>{o}</Select.Option>)}
              </Select>
            </Form.Item>

            <Form.Item label="Exclude Potential">
              <Select mode="multiple" placeholder="Exclude potential(s)" allowClear
                value={excludeLeadPotential} onChange={(v) => setExcludeLeadPotential(v?.length ? v : undefined)}
                style={{ width: '100%' }}
                loading={filterOptionsLoading}
              >
                {filterOptions.lead_potential.map(o => <Select.Option key={o} value={o}>{o}</Select.Option>)}
              </Select>
            </Form.Item>

            <Form.Item label="Exclude Added By">
              <Select mode="multiple" placeholder="Exclude person(s)" allowClear
                value={excludeAddedBy} onChange={(v) => setExcludeAddedBy(v?.length ? v : undefined)}
                style={{ width: '100%' }}
                loading={filterOptionsLoading}
              >
                {filterOptions.added_by.map(o => <Select.Option key={o} value={o}>{o}</Select.Option>)}
              </Select>
            </Form.Item>

            <Form.Item label="Exclude Country">
              <Select 
                mode="tags" 
                placeholder="Exclude country(ies)" 
                allowClear
                value={excludeCountry} 
                onChange={(v) => setExcludeCountry(v?.length ? v : undefined)}
                style={{ width: '100%' }}
                showSearch
                filterOption={(input, option) => {
                  const value = option?.value;
                  if (value && typeof value === 'string') {
                    return value.toLowerCase().includes(input.toLowerCase());
                  }
                  return false;
                }}
                loading={filterOptionsLoading}
                notFoundContent={filterOptionsLoading ? <Spin size="small" /> : 'No matching countries'}
              >
                {filterOptions.countries.map(country => (
                  <Select.Option key={country} value={country}>{country}</Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item label="Exclude City">
              <Select 
                mode="tags" 
                placeholder="Exclude city(ies)" 
                allowClear
                value={excludeCity} 
                onChange={(v) => setExcludeCity(v?.length ? v : undefined)}
                style={{ width: '100%' }}
                showSearch
                filterOption={(input, option) => {
                  const value = option?.value;
                  if (value && typeof value === 'string') {
                    return value.toLowerCase().includes(input.toLowerCase());
                  }
                  return false;
                }}
                loading={filterOptionsLoading}
                notFoundContent={filterOptionsLoading ? <Spin size="small" /> : 'No matching cities'}
              >
                {filterOptions.cities.map(city => (
                  <Select.Option key={city} value={city}>{city}</Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item label="Exclude Role">
              <Select 
                mode="tags" 
                placeholder="Exclude role(s)" 
                allowClear
                value={excludeRole} 
                onChange={(v) => setExcludeRole(v?.length ? v : undefined)}
                style={{ width: '100%' }}
                showSearch
                filterOption={(input, option) => {
                  const value = option?.value;
                  if (value && typeof value === 'string') {
                    return value.toLowerCase().includes(input.toLowerCase());
                  }
                  return false;
                }}
                loading={filterOptionsLoading}
                notFoundContent={filterOptionsLoading ? <Spin size="small" /> : 'No matching roles'}
              >
                {filterOptions.roles.map(role => (
                  <Select.Option key={role} value={role}>{role}</Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item label="Exclude Domain">
              <Select 
                mode="tags" 
                placeholder="Exclude domain(s)" 
                allowClear
                value={excludeDomain} 
                onChange={(v) => setExcludeDomain(v?.length ? v : undefined)}
                style={{ width: '100%' }}
                showSearch
                filterOption={(input, option) => {
                  const value = option?.value;
                  if (value && typeof value === 'string') {
                    return value.toLowerCase().includes(input.toLowerCase());
                  }
                  return false;
                }}
                loading={filterOptionsLoading}
                notFoundContent={filterOptionsLoading ? <Spin size="small" /> : 'No matching domains'}
              >
                {filterOptions.domains.map(domain => (
                  <Select.Option key={domain} value={domain}>{domain}</Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item label="Exclude Tags">
              <Select 
                mode="tags" 
                placeholder="Exclude tag(s)" 
                allowClear
                value={excludeTags} 
                onChange={(v) => setExcludeTags(v?.length ? v : undefined)}
                style={{ width: '100%' }}
                showSearch
                filterOption={(input, option) => {
                  const value = option?.value;
                  if (value && typeof value === 'string') {
                    return value.toLowerCase().includes(input.toLowerCase());
                  }
                  return false;
                }}
                loading={filterOptionsLoading}
                notFoundContent={filterOptionsLoading ? <Spin size="small" /> : 'No matching tags'}
              >
                {filterOptions.tags.map(tag => (
                  <Select.Option key={tag} value={tag}>{tag}</Select.Option>
                ))}
              </Select>
            </Form.Item>
          </div>

          <Button type="link" onClick={resetLeadFilters} style={{ padding: 0, marginTop: 8 }}>
            Clear All Filters
          </Button>
        </Form>
      </Drawer>

      <div style={{ opacity: notReady ? 0.45 : 1, pointerEvents: notReady ? 'none' : 'auto', transition: 'opacity 0.2s' }}>
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <Card title={<Space><ExperimentOutlined /><span>Dry Run</span></Space>} style={{ borderRadius: 12 }}>
            <Text>Analyze eligible leads without sending.</Text>
            {(getActiveLeadFilters() || manuallyDeselectedIds.size > 0) && (
              <div style={{ marginTop: 8 }}>
                <Tag color="blue"><FilterOutlined style={{ marginRight: 4 }} />Filtered: {effectiveLeadCount} leads selected</Tag>
                {manuallyDeselectedIds.size > 0 && (
                  <Tag color="orange" style={{ marginLeft: 4 }}>{manuallyDeselectedIds.size} manually excluded</Tag>
                )}
              </div>
            )}
            <div style={{ marginTop: 16 }}><Button type="primary" icon={<ExperimentOutlined />} onClick={handleDryRun} disabled={notReady}>Run Dry Run</Button></div>
            {dryRunResult && (
              <div style={{ marginTop: 24 }}><Divider /><Title level={5}>Results</Title>
                <Space direction="vertical">
                  <div><Text strong>Eligible: </Text>{dryRunResult.eligible_count}</div>
                  <div><Text strong>Already Sent (60-day): </Text>{dryRunResult.already_sent_this_campaign_count}</div>
                  <div><Text strong>Bounces: </Text><Text type="warning">{dryRunResult.bounced_count}</Text></div>
                  <div><Text strong>Unsubscribes: </Text><Text type="danger">{dryRunResult.unsubscribed_count + dryRunResult.do_not_send_count}</Text></div>
                  <div><Text strong>Global Cooldown (5-day): </Text>{dryRunResult.global_cooldown_count}</div>
                </Space>
              </div>
            )}
          </Card>

          <Card title={<Space><CheckCircleOutlined /><span>Test Send</span></Space>} style={{ borderRadius: 12 }}>
            <Text>Send test emails (bypasses eligibility checks).</Text>
            <div style={{ marginTop: 16 }}><Button icon={<CheckCircleOutlined />} onClick={handleOpenTestModal} disabled={notReady}>Send Test Email</Button></div>
            {testSendResult && <div style={{ marginTop: 16 }}><Divider /><Text strong>Sent: </Text>{testSendResult.sent_count} &nbsp;<Text strong>Failed: </Text>{testSendResult.failed_count}</div>}
          </Card>

          <Card title={<Space><SendOutlined /><span>Live Send</span></Space>} style={{ borderRadius: 12 }}>
            <Text>
              {(getActiveLeadFilters() || manuallyDeselectedIds.size > 0)
                ? `Send real emails to your selection of ${effectiveLeadCount} leads now, or schedule for later.`
                : 'Send real emails to all eligible leads now, or schedule for later.'
              }
            </Text>
            {(getActiveLeadFilters() || manuallyDeselectedIds.size > 0) && (
              <div style={{ marginTop: 8 }}>
                <Tag color="blue"><FilterOutlined style={{ marginRight: 4 }} />Selected: {effectiveLeadCount} leads</Tag>
                {manuallyDeselectedIds.size > 0 && (
                  <Tag color="orange" style={{ marginLeft: 4 }}>{manuallyDeselectedIds.size} manually excluded</Tag>
                )}
              </div>
            )}
            {/* Daily limit enforcement warning */}
            {selectedAccount && leadsExceedLimit && (
              <div style={{ marginTop: 12 }}>
                <Alert
                  type="warning"
                  showIcon
                  icon={<WarningOutlined />}
                  message="Daily send limit will be enforced"
                  description={
                    <span>
                      {effectiveLeadCount} leads match your selection but the sender account
                      {' '}<strong>{selectedAccount.display_name || selectedAccount.account_key}</strong> only has{' '}
                      <strong>{remainingDailyQuota}</strong> send{remainingDailyQuota !== 1 ? 's' : ''} remaining today
                      (limit: {selectedAccount.daily_send_limit}, used: {selectedAccount.current_daily_sends}).
                      {remainingDailyQuota > 0 ? (
                        <span>
                          {' '}Only the first <strong>{remainingDailyQuota}</strong> eligible lead{remainingDailyQuota !== 1 ? 's' : ''} will receive emails.
                          The remaining <strong>{effectiveLeadCount - remainingDailyQuota}</strong> will be skipped due to the daily limit.
                        </span>
                      ) : (
                        <span> No emails can be sent until the limit resets.</span>
                      )}
                    </span>
                  }
                  style={{ fontSize: 13 }}
                />
              </div>
            )}
            {selectedAccount && remainingDailyQuota === 0 && (
              <div style={{ marginTop: 12 }}>
                <Alert
                  type="error"
                  showIcon
                  message="Daily send limit reached"
                  description={`The sender account ${selectedAccount.display_name || selectedAccount.account_key} has used all ${selectedAccount.daily_send_limit} sends for today. No emails will be sent until the limit resets.`}
                />
              </div>
            )}
            <div style={{ marginTop: 16 }}>
              <Space>
                <Popconfirm
                  title="Confirm Live Send"
                  description="Are you sure you want to send emails now? This cannot be undone."
                  onConfirm={handleLiveSend}
                  okText="Yes, Send Now"
                  cancelText="Cancel"
                  okButtonProps={{ danger: true }}
                  disabled={notReady || isSendJobActive}
                >
                  <Button
                    type="primary"
                    danger
                    icon={<SendOutlined />}
                    disabled={notReady || isSendJobActive}
                    loading={liveSendLoading}
                  >
                    {isSendJobActive ? 'Sending…' : 'Send Now'}
                  </Button>
                </Popconfirm>
                <Button type="default" icon={<ClockCircleOutlined />} onClick={handleOpenScheduleModal} disabled={notReady || isSendJobActive}>
                  Schedule Send
                </Button>
              </Space>
            </div>
            {liveSendResult && (
              <div style={{ marginTop: 16 }}><Divider />
                <Space direction="vertical">
                  <div><Text strong>Sent: </Text>{liveSendResult.sent_count}</div>
                  <div><Text strong>Failed: </Text>{liveSendResult.failed_count}</div>
                  <div><Text strong>Skipped: </Text>{liveSendResult.skipped_count}</div>
                </Space>
              </div>
            )}

            {/* Email Preview */}
            {!notReady && (
              <div style={{ marginTop: 20 }}>
                <Divider style={{ margin: '16px 0' }} />
                <Space style={{ marginBottom: 8 }}>
                  <EyeOutlined />
                  <Text strong>Email Preview</Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>(sample with name &quot;John&quot;)</Text>
                </Space>
                {previewLoading ? (
                  <div style={{ textAlign: 'center', padding: 24 }}><Spin /></div>
                ) : previewData ? (
                  <div style={{ border: isDark ? '1px solid #434343' : '1px solid #e8e8e8', borderRadius: 8, overflow: 'hidden' }}>
                    <div style={{ padding: '10px 16px', background: isDark ? '#1f1f1f' : '#fafafa', borderBottom: isDark ? '1px solid #434343' : '1px solid #e8e8e8' }}>
                      <Text strong>Subject: </Text><Text>{previewData.subject}</Text>
                    </div>
                    <div
                      style={{ padding: 16, fontSize: 13, lineHeight: '1.6', background: isDark ? '#141414' : '#fff' }}
                      dangerouslySetInnerHTML={{ __html: previewData.body }}
                    />
                  </div>
                ) : (
                  <Text type="secondary">Select a campaign and sender account to see a preview.</Text>
                )}
              </div>
            )}
          </Card>
        </Space>
      </div>

      {notReady && (
        <div style={{ textAlign: 'center', marginTop: -8, marginBottom: 16 }}>
          <Text type="secondary">
            {!selectedCampaignId
              ? 'Select a campaign above to continue.'
              : 'Select a sender account to enable dry run, test send, and live send options.'}
          </Text>
        </div>
      )}
      </>
      )}

      {/* ==================== JOBS TAB ==================== */}
      {activeTab === 'jobs' && (
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        {/* Scheduled Sends (upcoming + completed recent) */}
        {scheduledSends.length > 0 && (
          <Card
            title={
              <Space>
                <CalendarOutlined />
                <span>Scheduled Sends</span>
                <Badge
                  count={scheduledSends.filter(s => s.status === 'pending').length}
                  style={{ backgroundColor: '#1890ff' }}
                />
              </Space>
            }
            style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}
          >
            <Table
              columns={scheduledColumns}
              dataSource={scheduledSends}
              rowKey="id"
              loading={scheduledSendsLoading}
              pagination={false}
              size="small"
            />
          </Card>
        )}

        <Card
          title={
            <Space>
              <UnorderedListOutlined />
              <span>Send Jobs</span>
              {runningJobsCount > 0 && (
                <Badge count={runningJobsCount} size="small" style={{ backgroundColor: '#1890ff' }} />
              )}
            </Space>
          }
          extra={
            <Button
              type="text"
              icon={<ReloadOutlined />}
              onClick={() => fetchSendJobs(true)}
              loading={sendJobsLoading}
              size="small"
            >
              Refresh
            </Button>
          }
          style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}
        >
          {sendJobsLoading && sendJobs.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40 }}>
              <Spin size="large" />
              <div style={{ marginTop: 12 }}><Text type="secondary">Loading jobs...</Text></div>
            </div>
          ) : sendJobs.length === 0 ? (
            <Empty
              description="No send jobs yet"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          ) : (
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
              {sendJobs.map(job => {
                const total = job.total_eligible || 0;
                const processed = (job.sent_count || 0) + (job.failed_count || 0);
                const pct = total > 0 ? Math.round((processed / total) * 100) : 0;
                const isRunning = job.status === 'running' || job.status === 'cancelling';
                const isDone = job.status === 'completed' || job.status === 'failed' || job.status === 'cancelled';

                // ETA
                let etaText = '';
                if (job.status === 'running' && processed > 0 && job.started_at) {
                  const elapsed = (Date.now() - new Date(job.started_at).getTime()) / 1000;
                  const perItem = elapsed / processed;
                  const remaining = Math.max(total - processed, 0);
                  const etaSec = Math.round(perItem * remaining);
                  etaText = etaSec > 60 ? `~${Math.ceil(etaSec / 60)}m left` : `~${etaSec}s left`;
                }

                const statusColors: Record<string, string> = {
                  running: 'processing', completed: 'success', failed: 'error',
                  cancelled: 'warning', cancelling: 'processing',
                };

                const statusIcons: Record<string, React.ReactNode> = {
                  running: <LoadingOutlined spin />,
                  cancelling: <LoadingOutlined spin />,
                  completed: <CheckCircleOutlined />,
                  failed: <StopOutlined />,
                  cancelled: <StopOutlined />,
                };

                return (
                  <Card
                    key={job.job_id}
                    size="small"
                    style={{
                      borderRadius: 8,
                      border: isRunning
                        ? (isDark ? '1px solid rgba(24, 144, 255, 0.4)' : '1px solid #91caff')
                        : (isDark ? '1px solid #434343' : '1px solid #f0f0f0'),
                      background: isRunning
                        ? (isDark ? 'rgba(24, 144, 255, 0.05)' : '#f0f5ff')
                        : undefined,
                    }}
                  >
                    {/* Header row */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                      <div>
                        <Space size={8} align="center">
                          <Badge status={statusColors[job.status] as any || 'default'} />
                          <Text strong style={{ fontSize: 15 }}>
                            {job.campaign_name || 'Unknown Campaign'}
                          </Text>
                          <Tag color={
                            job.status === 'running' ? 'blue' :
                            job.status === 'cancelling' ? 'orange' :
                            job.status === 'completed' ? 'green' :
                            job.status === 'failed' ? 'red' :
                            job.status === 'cancelled' ? 'gold' : 'default'
                          }>
                            {statusIcons[job.status]} {job.status.toUpperCase()}
                          </Tag>
                        </Space>
                        <div style={{ marginTop: 4 }}>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            Sender: {job.sender_account_name || 'Unknown'} ({job.sender_account_email || '?'})
                          </Text>
                        </div>
                        <div style={{ marginTop: 2 }}>
                          <Text type="secondary" style={{ fontSize: 11 }}>
                            <HistoryOutlined style={{ marginRight: 4 }} />
                            Started: {dayjs(job.started_at).format('MMM D, YYYY h:mm:ss A')}
                            {job.completed_at && (
                              <span> · Ended: {dayjs(job.completed_at).format('h:mm:ss A')}</span>
                            )}
                          </Text>
                        </div>
                        {job.lead_filters && Object.keys(job.lead_filters).length > 0 && (
                          <div style={{ marginTop: 4 }}>
                            <Text type="secondary" style={{ fontSize: 11 }}>
                              <FilterOutlined style={{ marginRight: 4 }} />
                              Filters: {Object.entries(job.lead_filters).map(([k, v]) =>
                                `${k}: ${Array.isArray(v) ? v.join(', ') : v}`
                              ).join(' · ')}
                            </Text>
                          </div>
                        )}
                      </div>
                      <div>
                        {(job.status === 'running') && (
                          <Popconfirm
                            title="Stop this send job?"
                            description="Only emails already sent will count. Pending emails will not be sent."
                            onConfirm={() => handleCancelJob(job.job_id)}
                            okText="Stop"
                            cancelText="No"
                            okButtonProps={{ danger: true }}
                          >
                            <Button size="small" danger icon={<StopOutlined />}>
                              Stop
                            </Button>
                          </Popconfirm>
                        )}
                        {job.status === 'cancelling' && (
                          <Button size="small" disabled loading>Stopping...</Button>
                        )}
                      </div>
                    </div>

                    {/* Progress bar for running jobs */}
                    {(isRunning || isDone) && total > 0 && (
                      <div style={{ marginBottom: 8 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                          <Text style={{ fontSize: 12 }}>
                            {processed} / {total} processed
                          </Text>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {isRunning ? etaText : ''}
                          </Text>
                        </div>
                        <Progress
                          percent={isDone ? 100 : pct}
                          status={
                            job.status === 'failed' ? 'exception' :
                            job.status === 'cancelled' ? 'normal' :
                            isDone ? 'success' : 'active'
                          }
                          strokeColor={job.status === 'cancelled' ? '#faad14' : undefined}
                          size="small"
                        />
                      </div>
                    )}

                    {/* Current email for running jobs */}
                    {isRunning && job.current_email && (
                      <div style={{
                        padding: '4px 10px', marginBottom: 8,
                        background: isDark ? 'rgba(24, 144, 255, 0.1)' : '#e6f7ff',
                        borderRadius: 4,
                        border: isDark ? '1px solid rgba(24, 144, 255, 0.3)' : '1px solid #91d5ff',
                      }}>
                        <LoadingOutlined style={{ marginRight: 6, color: '#1890ff', fontSize: 11 }} spin />
                        <Text style={{ fontSize: 12 }}>
                          {job.current_email.startsWith('⏳') ? (
                            <Text type="secondary">{job.current_email}</Text>
                          ) : (
                            <>Sending to <Text strong>{job.current_email}</Text></>
                          )}
                        </Text>
                      </div>
                    )}

                    {/* Stats grid */}
                    <div style={{
                      display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 8,
                      textAlign: 'center',
                      padding: 8, background: isDark ? '#1f1f1f' : '#fafafa', borderRadius: 6,
                      border: isDark ? '1px solid #434343' : 'none',
                    }}>
                      <div>
                        <div style={{ fontSize: 16, fontWeight: 700, color: '#52c41a' }}>{job.sent_count}</div>
                        <Text type="secondary" style={{ fontSize: 10 }}>Sent</Text>
                      </div>
                      <div>
                        <div style={{ fontSize: 16, fontWeight: 700, color: '#ff4d4f' }}>{job.failed_count}</div>
                        <Text type="secondary" style={{ fontSize: 10 }}>Failed</Text>
                      </div>
                      <div>
                        <div style={{ fontSize: 16, fontWeight: 700, color: '#1890ff' }}>{Math.max(total - processed, 0)}</div>
                        <Text type="secondary" style={{ fontSize: 10 }}>Remaining</Text>
                      </div>
                      <div>
                        <div style={{ fontSize: 16, fontWeight: 700, color: isDark ? '#aaa' : '#666' }}>{total}</div>
                        <Text type="secondary" style={{ fontSize: 10 }}>Total</Text>
                      </div>
                    </div>

                    {/* Error message */}
                    {job.status === 'failed' && job.error && (
                      <div style={{ marginTop: 8 }}>
                        <Alert type="error" showIcon message={job.error} style={{ fontSize: 12 }} />
                      </div>
                    )}
                    {job.status === 'cancelled' && (
                      <div style={{ marginTop: 8 }}>
                        <Alert type="warning" showIcon
                          message={`Stopped. ${job.sent_count} email${job.sent_count !== 1 ? 's' : ''} sent before cancellation.`}
                          style={{ fontSize: 12 }}
                        />
                      </div>
                    )}
                    {job.status === 'completed' && (
                      <div style={{ marginTop: 8 }}>
                        <Alert type="success" showIcon
                          message={`Completed! ${job.sent_count} email${job.sent_count !== 1 ? 's' : ''} sent successfully.`}
                          style={{ fontSize: 12 }}
                        />
                      </div>
                    )}
                  </Card>
                );
              })}
            </Space>
          )}
        </Card>
      </Space>
      )}

      {/* Test Send Modal */}
      <Modal
        title="Send Test Email"
        open={testModalVisible}
        onCancel={() => { setTestModalVisible(false); setTestEmails(''); }}
        footer={[
          <Button
            key="cancel"
            onClick={() => { setTestModalVisible(false); setTestEmails(''); }}
            disabled={testSendLoading}
          >
            Cancel
          </Button>,
          <Button
            key="ok"
            type="primary"
            onClick={handleTestSendClick}
            loading={testSendLoading}
          >
            Send
          </Button>,
        ]}
        width={700}
      >
        <Text>Enter email addresses (one per line, max 10):</Text>
        <Input.TextArea
          rows={4}
          value={testEmails}
          onChange={e => setTestEmails(e.target.value)}
          placeholder="email1@example.com&#10;email2@example.com&#10;email3@example.com"
          style={{ marginTop: 8 }}
        />

        <Divider />
        <div>
          <Space style={{ marginBottom: 8 }}>
            <EyeOutlined />
            <Text strong>Email Preview</Text>
            <Text type="secondary" style={{ fontSize: 12 }}>(sample with name "John")</Text>
          </Space>
          {previewLoading ? (
            <div style={{ textAlign: 'center', padding: 24 }}><Spin /></div>
          ) : previewData ? (
            <div style={{ border: isDark ? '1px solid #434343' : '1px solid #e8e8e8', borderRadius: 8, overflow: 'hidden' }}>
              <div style={{ padding: '10px 16px', background: isDark ? '#1f1f1f' : '#fafafa', borderBottom: isDark ? '1px solid #434343' : '1px solid #e8e8e8' }}>
                <Text strong>Subject: </Text><Text>{previewData.subject}</Text>
              </div>
              <div
                style={{ padding: 16, fontSize: 13, lineHeight: '1.6', background: isDark ? '#141414' : '#fff' }}
                dangerouslySetInnerHTML={{ __html: previewData.body }}
              />
            </div>
          ) : (
            <Text type="secondary">No preview available.</Text>
          )}
        </div>
      </Modal>

      {/* Schedule Send Modal */}
      <Modal
        title={
          <Space>
            <ClockCircleOutlined />
            <span>Schedule Campaign Send</span>
          </Space>
        }
        open={scheduleModalVisible}
        onCancel={() => setScheduleModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setScheduleModalVisible(false)}>Cancel</Button>,
          <Button
            key="schedule"
            type="primary"
            icon={<ClockCircleOutlined />}
            onClick={handleScheduleSend}
            loading={scheduleLoading}
            disabled={!scheduledDateTime}
          >
            Schedule Send
          </Button>,
        ]}
        width={500}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <div>
            <Text strong style={{ display: 'block', marginBottom: 8 }}>Campaign</Text>
            <Text>{selected?.name} ({selected?.campaign_id})</Text>
          </div>
          <div>
            <Text strong style={{ display: 'block', marginBottom: 8 }}>Sender Account</Text>
            <Text>{selectedAccount?.display_name || selectedAccount?.account_key} ({selectedAccount?.email})</Text>
          </div>
          <div>
            <Text strong style={{ display: 'block', marginBottom: 8 }}>Schedule Date & Time</Text>
            <DatePicker
              showTime={{ format: 'HH:mm' }}
              format="YYYY-MM-DD HH:mm"
              style={{ width: '100%' }}
              value={scheduledDateTime}
              onChange={setScheduledDateTime}
              disabledDate={(current) => current && current < dayjs().startOf('day')}
              placeholder="Select date and time"
            />
          </div>
          <div>
            <Text strong style={{ display: 'block', marginBottom: 8 }}>Max Sends (optional)</Text>
            <InputNumber
              style={{ width: '100%' }}
              placeholder="No limit"
              min={1}
              value={scheduleMaxSends}
              onChange={v => setScheduleMaxSends(v || undefined)}
            />
          </div>
        </Space>
      </Modal>

      {/* Floating Send Progress Popup */}
      {progressVisible && (() => {
        const p = sendProgress;
        const total = p?.total_eligible || 0;
        const processed = (p?.sent_count || 0) + (p?.failed_count || 0);
        const pct = total > 0 ? Math.round((processed / total) * 100) : 0;
        const remaining = Math.max(total - processed, 0);
        const isRunning = p?.status === 'running' || p?.status === 'cancelling';
        const isDone = p?.status === 'completed' || p?.status === 'failed' || p?.status === 'cancelled';

        // ETA calculation
        let etaText = '';
        if (p && p.status === 'running' && processed > 0 && p.started_at) {
          const elapsed = (Date.now() - new Date(p.started_at).getTime()) / 1000;
          const perItem = elapsed / processed;
          const etaSec = Math.round(perItem * remaining);
          etaText = etaSec > 60 ? `~${Math.ceil(etaSec / 60)}m left` : `~${etaSec}s left`;
        }

        const dismissPopup = () => {
          setProgressVisible(false);
          setSendJobId(null);
          setSendProgress(null);
        };

        // -- Minimized view: compact bar --
        if (progressMinimized) {
          return (
            <div style={{
              position: 'fixed', bottom: 24, right: 24, zIndex: 9999,
              width: 340, borderRadius: 10,
              background: isDark ? '#1f1f1f' : '#fff', 
              boxShadow: isDark ? '0 6px 24px rgba(0,0,0,0.4)' : '0 6px 24px rgba(0,0,0,0.15)',
              border: isDark ? '1px solid #434343' : '1px solid #e8e8e8', 
              overflow: 'hidden',
              transition: 'all 0.25s ease',
            }}>
              <div
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '10px 14px', cursor: 'pointer',
                  background: isDone
                    ? (p?.status === 'completed' 
                        ? (isDark ? 'rgba(82, 196, 26, 0.1)' : '#f6ffed')
                        : p?.status === 'failed' 
                        ? (isDark ? 'rgba(255, 77, 79, 0.1)' : '#fff2f0')
                        : (isDark ? 'rgba(250, 173, 20, 0.1)' : '#fffbe6'))
                    : (isDark ? 'rgba(24, 144, 255, 0.1)' : '#f0f5ff'),
                }}
                onClick={() => setProgressMinimized(false)}
              >
                <Space size={8}>
                  {isRunning && <LoadingOutlined spin style={{ color: '#1890ff' }} />}
                  {p?.status === 'completed' && <CheckCircleOutlined style={{ color: '#52c41a' }} />}
                  {p?.status === 'failed' && <StopOutlined style={{ color: '#ff4d4f' }} />}
                  {p?.status === 'cancelled' && <StopOutlined style={{ color: '#faad14' }} />}
                  <Text strong style={{ fontSize: 13 }}>
                    {isRunning ? `Sending... ${pct}%` : p?.status === 'completed' ? 'Send Complete' : p?.status === 'cancelled' ? 'Send Stopped' : 'Send Failed'}
                  </Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {p ? `${p.sent_count} sent` : ''}
                  </Text>
                </Space>
                <Space size={4}>
                  <Button type="text" size="small" icon={<ExpandOutlined />}
                    onClick={e => { e.stopPropagation(); setProgressMinimized(false); }}
                    style={{ fontSize: 12, width: 24, height: 24 }}
                  />
                  {isDone && (
                    <Button type="text" size="small" icon={<CloseOutlined />}
                      onClick={e => { e.stopPropagation(); dismissPopup(); }}
                      style={{ fontSize: 12, width: 24, height: 24 }}
                    />
                  )}
                </Space>
              </div>
              {isRunning && (
                <Progress percent={pct} showInfo={false} size="small"
                  style={{ margin: 0, lineHeight: 0 }}
                  strokeColor="#1890ff"
                  status="active"
                />
              )}
            </div>
          );
        }

        // -- Expanded view --
        return (
          <div style={{
            position: 'fixed', bottom: 24, right: 24, zIndex: 9999,
            width: 400, borderRadius: 12,
            background: isDark ? '#1f1f1f' : '#fff', 
            boxShadow: isDark ? '0 8px 32px rgba(0,0,0,0.4)' : '0 8px 32px rgba(0,0,0,0.18)',
            border: isDark ? '1px solid #434343' : '1px solid #e0e0e0', 
            overflow: 'hidden',
            transition: 'all 0.25s ease',
          }}>
            {/* Header */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '10px 16px',
              background: isDone
                ? (p?.status === 'completed' 
                    ? (isDark ? 'rgba(82, 196, 26, 0.1)' : '#f6ffed')
                    : p?.status === 'failed' 
                    ? (isDark ? 'rgba(255, 77, 79, 0.1)' : '#fff2f0')
                    : (isDark ? 'rgba(250, 173, 20, 0.1)' : '#fffbe6'))
                : (isDark ? 'rgba(24, 144, 255, 0.1)' : '#f0f5ff'),
              borderBottom: isDark ? '1px solid #434343' : '1px solid #e8e8e8',
            }}>
              <Space size={8}>
                <SendOutlined />
                <Text strong style={{ fontSize: 14 }}>
                  {isRunning ? 'Sending Emails' : p?.status === 'completed' ? 'Send Complete' : p?.status === 'cancelled' ? 'Send Stopped' : 'Send Failed'}
                </Text>
              </Space>
              <Space size={2}>
                <Button type="text" size="small" icon={<MinusOutlined />}
                  onClick={() => setProgressMinimized(true)}
                  title="Minimize"
                  style={{ width: 28, height: 28 }}
                />
                {isDone && (
                  <Button type="text" size="small" icon={<CloseOutlined />}
                    onClick={dismissPopup}
                    title="Close"
                    style={{ width: 28, height: 28 }}
                  />
                )}
              </Space>
            </div>

            {/* Body */}
            <div style={{ padding: 16 }}>
              {!p ? (
                <div style={{ textAlign: 'center', padding: 16 }}>
                  <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
                  <div style={{ marginTop: 8 }}><Text type="secondary">Starting send job...</Text></div>
                </div>
              ) : (
                <>
                  {/* Progress bar */}
                  <div style={{ marginBottom: 14 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                      <Text style={{ fontSize: 12 }}>{processed} / {total}</Text>
                      <Text type="secondary" style={{ fontSize: 12 }}>{etaText}</Text>
                    </div>
                    <Progress
                      percent={isDone ? 100 : pct}
                      status={p.status === 'failed' ? 'exception' : isDone ? 'success' : 'active'}
                      strokeColor={p.status === 'cancelled' ? '#faad14' : undefined}
                      size="small"
                    />
                  </div>

                  {/* Current email */}
                  {isRunning && p.current_email && (
                    <div style={{
                      marginBottom: 12, padding: '6px 10px', 
                      background: isDark ? 'rgba(24, 144, 255, 0.1)' : '#f0f5ff',
                      borderRadius: 6, 
                      border: isDark ? '1px solid rgba(24, 144, 255, 0.3)' : '1px solid #d6e4ff',
                    }}>
                      <LoadingOutlined style={{ marginRight: 6, color: '#1890ff', fontSize: 12 }} spin />
                      <Text style={{ fontSize: 12 }}>
                        Sending to <Text strong>{p.current_email}</Text>
                      </Text>
                    </div>
                  )}

                  {/* Stats */}
                  <div style={{
                    display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 8,
                    textAlign: 'center', marginBottom: 12,
                    padding: 10, background: isDark ? '#1f1f1f' : '#fafafa', borderRadius: 8,
                    border: isDark ? '1px solid #434343' : 'none',
                  }}>
                    <div>
                      <div style={{ fontSize: 18, fontWeight: 700, color: '#52c41a' }}>{p.sent_count}</div>
                      <Text type="secondary" style={{ fontSize: 10 }}>Sent</Text>
                    </div>
                    <div>
                      <div style={{ fontSize: 18, fontWeight: 700, color: '#ff4d4f' }}>{p.failed_count}</div>
                      <Text type="secondary" style={{ fontSize: 10 }}>Failed</Text>
                    </div>
                    <div>
                      <div style={{ fontSize: 18, fontWeight: 700, color: '#1890ff' }}>{remaining}</div>
                      <Text type="secondary" style={{ fontSize: 10 }}>Left</Text>
                    </div>
                    <div>
                      <div style={{ fontSize: 18, fontWeight: 700, color: '#666' }}>{total}</div>
                      <Text type="secondary" style={{ fontSize: 10 }}>Total</Text>
                    </div>
                  </div>

                  {(p.skipped_count > 0 || p.daily_limit_capped_count > 0) && (
                    <div style={{ marginBottom: 8 }}>
                      {p.skipped_count > 0 && (
                        <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>
                          {p.skipped_count} skipped (already sent / suppressed / cooldown)
                        </Text>
                      )}
                      {p.daily_limit_capped_count > 0 && (
                        <Text type="warning" style={{ fontSize: 11, display: 'block' }}>
                          <WarningOutlined style={{ marginRight: 4 }} />
                          {p.daily_limit_capped_count} lead{p.daily_limit_capped_count !== 1 ? 's' : ''} excluded due to sender daily limit
                        </Text>
                      )}
                    </div>
                  )}

                  {/* Status messages */}
                  {p.status === 'completed' && (
                    <Alert type="success" showIcon style={{ fontSize: 12 }}
                      message={`Done! ${p.sent_count} email${p.sent_count !== 1 ? 's' : ''} sent successfully.`}
                      description={p.daily_limit_capped_count > 0
                        ? `${p.daily_limit_capped_count} additional lead${p.daily_limit_capped_count !== 1 ? 's were' : ' was'} not sent due to the sender account's daily limit.`
                        : undefined
                      }
                    />
                  )}
                  {p.status === 'cancelled' && (
                    <Alert type="warning" showIcon style={{ fontSize: 12 }}
                      message={`Stopped. ${p.sent_count} sent before cancellation.`}
                    />
                  )}
                  {p.status === 'failed' && (
                    <Alert type="error" showIcon style={{ fontSize: 12 }}
                      message={p.error || 'Unknown error'}
                    />
                  )}

                  {/* Action button */}
                  <div style={{ marginTop: 12, textAlign: 'right' }}>
                    {p.status === 'running' && (
                      <Button size="small" danger icon={<StopOutlined />} onClick={handleCancelLiveSend}>
                        Stop Sending
                      </Button>
                    )}
                    {p.status === 'cancelling' && (
                      <Button size="small" disabled loading>Cancelling...</Button>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        );
      })()}
    </div>
  );
};
